/**
 * SettingsView.js — Portal Settings tab (owner only)
 *
 * Mirrors the OwnerSettings screen from the native POS app.
 * Reads/writes directly to Firestore — same fields, same paths.
 *
 * Excluded from portal (device-local / not web-applicable):
 *   - Printer settings (Bluetooth scan, LAN assignment)
 *
 * Sub-tabs: General | Pickup | Messaging | Stripe | Courses | Devices | Billing
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc, setDoc, collection, getDocs, query, where, serverTimestamp, Timestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase';
import UnsavedChangesModal from '../../utilities/UnsavedChangesModal';
import AuditLogView from './AuditLogView';
import { writePortalAuditEvent, PORTAL_AUDIT } from '../../utilities/portalAuditLog';

const functions = getFunctions();
const createStripeLoginLink    = httpsCallable(functions, 'createStripeLoginLink');
const createStripeRestaurantFn = httpsCallable(functions, 'createStripeRestaurant');

// ── Message type definitions ──────────────────────────────────────────────────

const MSG_TYPES = [
  { id: 'reservationConfirmation', label: 'Reservation Confirmation' },
  { id: 'reservationPreorderNotice', label: 'Reservation — Preorder Link' },
  { id: 'waitlistConfirmation',    label: 'Waitlist Confirmation'    },
  { id: 'waitlistPreorderNotice',  label: 'Waitlist — Preorder Link' },
  { id: 'waitlistEtaUpdate',       label: 'Waitlist — ETA Update'    },
  { id: 'tableReady',              label: 'Table Ready'              },
  { id: 'reminderFollowUp',        label: 'Reminder / Follow Up'     },
  { id: 'autoDrop',                label: 'Auto-Drop Reservation'    },
];

// Per-type token chips — only tokens the cloud function actually injects.
const MSG_TYPE_TOKENS = {
  reservationConfirmation: [
    { label: 'Customer Name',   token: '{customerName}'      },
    { label: 'Restaurant Name', token: '{restaurantName}'    },
    { label: 'Party Size',      token: '{partySize}'         },
    { label: 'Time',            token: '{time}'              },
    { label: 'Date',            token: '{date}'              },
    { label: 'Table',           token: '{table}'             },
    { label: 'Reservation ID',  token: '{reservationId}'     },
    { label: 'Preorder Block',  token: '{manageLinkSection}' },
  ],
  waitlistConfirmation: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Party Size',      token: '{partySize}'      },
    { label: 'Est. Wait (min)', token: '{estimatedWait}'  },
    { label: 'Waitlist ID',     token: '{waitlistId}'     },
  ],
  reservationPreorderNotice: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Reservation URL', token: '{reservationUrl}' },
  ],
  waitlistPreorderNotice: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Preorder URL',    token: '{preorderUrl}'    },
  ],
  waitlistEtaUpdate: [
    { label: 'Customer Name',   token: '{customerName}'  },
    { label: 'Est. Wait (min)', token: '{estimatedWait}' },
  ],
  tableReady: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Party Size',      token: '{partySize}'      },
    { label: 'Table Type',      token: '{tableType}'      },
  ],
  reminderFollowUp: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Party Size',      token: '{partySize}'      },
    { label: 'Time',            token: '{time}'           },
    { label: 'Date',            token: '{date}'           },
    { label: 'Table',           token: '{table}'          },
  ],
  autoDrop: [
    { label: 'Customer Name',   token: '{customerName}'   },
    { label: 'Restaurant Name', token: '{restaurantName}' },
    { label: 'Party Size',      token: '{partySize}'      },
    { label: 'Time',            token: '{time}'           },
    { label: 'Date',            token: '{date}'           },
    { label: 'Table',           token: '{table}'          },
  ],
};

// ── Shared UI ─────────────────────────────────────────────────────────────────

const SectionHeader = ({ title, description }) => (
  <div className="mb-4">
    <p className="text-sm font-semibold text-neutral-800">{title}</p>
    {description && <p className="text-sm text-neutral-400 mt-0.5">{description}</p>}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white border border-neutral-200 rounded-xl p-5 ${className}`}>
    {children}
  </div>
);

/**
 * SettingRow — a labeled boolean toggle that saves instantly on change.
 * `onToggle(newValue)` is called by the parent.
 */
const SettingRow = ({ label, description, value, onToggle, saving }) => (
  <div className="flex items-center justify-between gap-4 py-3 border-b border-neutral-100 last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-medium text-neutral-700">{label}</p>
      {description && <p className="text-sm text-neutral-400 mt-0.5">{description}</p>}
    </div>
    <div className="flex gap-1.5 shrink-0">
      <button
        onClick={() => onToggle(true)}
        disabled={saving}
        className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${value ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
      >
        Yes
      </button>
      <button
        onClick={() => onToggle(false)}
        disabled={saving}
        className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${!value ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
      >
        No
      </button>
    </div>
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────

const SettingsView = ({ restaurantId, user }) => {
  const [subTab, setSubTab]           = useState('general');
  const [pendingSubTab, setPendingSubTab] = useState(null);
  const [guardSaving, setGuardSaving] = useState(false);
  const msgSaveFnRef = useRef(null);
  const msgDirtyRef  = useRef(false);

  const handleMsgDirtyChange = (isDirty, saveFn) => {
    msgDirtyRef.current  = isDirty;
    msgSaveFnRef.current = isDirty ? saveFn : null;
  };

  const handleSubTabClick = (t) => {
    if (t === subTab) return;
    // Only guard when leaving the messaging tab with unsaved changes
    if (subTab === 'messaging' && msgDirtyRef.current) {
      setPendingSubTab(t);
    } else {
      setSubTab(t);
    }
  };

  const handleGuardSave = async () => {
    setGuardSaving(true);
    try {
      await msgSaveFnRef.current?.();
      msgDirtyRef.current  = false;
      msgSaveFnRef.current = null;
      setSubTab(pendingSubTab);
      setPendingSubTab(null);
    } catch (_) { /* keep modal open */ }
    finally { setGuardSaving(false); }
  };

  const handleGuardLeave = () => {
    msgDirtyRef.current  = false;
    msgSaveFnRef.current = null;
    setSubTab(pendingSubTab);
    setPendingSubTab(null);
  };

  const handleGuardStay = () => setPendingSubTab(null);

  return (
    <div className="space-y-4">
      {/* Sub-tab bar */}
      <div className="flex gap-1 border-b border-neutral-200 overflow-x-auto">
        {['general', 'enforcement', 'pickup', 'messaging', 'stripe', 'courses', 'billing', 'events'].map(t => (
          <button
            key={t}
            onClick={() => handleSubTabClick(t)}
            className={`shrink-0 px-5 py-2 text-sm font-medium capitalize border-b-2 transition-colors ${
              subTab === t
                ? 'border-neutral-900 text-neutral-900'
                : 'border-transparent text-neutral-400 hover:text-neutral-600'
            }`}
          >
            {t === 'events' ? 'Events Log' : t}
          </button>
        ))}
      </div>

      {subTab === 'general'      && <GeneralTab      restaurantId={restaurantId} user={user} />}
      {subTab === 'enforcement'  && <EnforcementTab  restaurantId={restaurantId} user={user} />}
      {subTab === 'messaging'    && <MessagingTab    restaurantId={restaurantId} user={user} onDirtyChange={handleMsgDirtyChange} />}
      {subTab === 'stripe'    && <StripeTab     restaurantId={restaurantId} user={user} />}
      {subTab === 'pickup'    && <PickupTab      restaurantId={restaurantId} user={user} />}
      {subTab === 'courses'   && <CoursesTab    restaurantId={restaurantId} user={user} />}
      {subTab === 'billing'   && <BillingTab    restaurantId={restaurantId} />}
      {subTab === 'events'    && <AuditLogView  restaurantId={restaurantId} />}

      {pendingSubTab !== null && (
        <UnsavedChangesModal
          saving={guardSaving}
          onSave={handleGuardSave}
          onLeave={handleGuardLeave}
          onStay={handleGuardStay}
        />
      )}
    </div>
  );
};

// ── General Tab ───────────────────────────────────────────────────────────────

const GeneralTab = ({ restaurantId, user }) => {
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');

  // Settings state
  const [restaurantName, setRestaurantName]             = useState('');
  const [savingName, setSavingName]                     = useState(false);
  const [nameSaved, setNameSaved]                       = useState(false);

  const [address, setAddress]                           = useState('');
  const [savingAddress, setSavingAddress]               = useState(false);
  const [addressSaved, setAddressSaved]                 = useState(false);

  const [managerKey, setManagerKey]                     = useState('');
  const [newManagerKey, setNewManagerKey]               = useState('');
  const [savingKey, setSavingKey]                       = useState(false);
  const [keySaved, setKeySaved]                         = useState(false);

  const [allowWebReservations, setAllowWebReservations] = useState(false);
  const [allowOnlineOrders, setAllowOnlineOrders]       = useState(false);
  const [allowReservationOrders, setAllowReservationOrders] = useState(false);
  const [allowWaitlistPreOrders, setAllowWaitlistPreOrders] = useState(false);
  const [allowSameDayReservations, setAllowSameDayReservations] = useState(false);
  const [sameDayBufferHours, setSameDayBufferHours]     = useState(0);
  const [allowNewGiftCards, setAllowNewGiftCards]       = useState(false);
  const [reservationLength, setReservationLength]       = useState(60);
  const [averageReservation, setAverageReservation]     = useState(90);
  const [avgTurnMins, setAvgTurnMins]                   = useState(null);
  const [avgTurnLoading, setAvgTurnLoading]             = useState(true);

  // Reminder SMS
  const [allowReminder, setAllowReminder]               = useState(false);
  const [reminderMinutes, setReminderMinutes]           = useState(60);

  // Auto-drop SMS
  const [allowAutoDrop, setAllowAutoDrop]               = useState(false);
  const [autoDropMinutes, setAutoDropMinutes]           = useState(15);
  const [autoDropMarkNoShow, setAutoDropMarkNoShow]     = useState(false);

  // Day-before confirmation texts
  const [confirmationTextsEnabled, setConfirmationTextsEnabled] = useState(false);
  const [confirmationSendHour, setConfirmationSendHour]         = useState(10);

  // Floor action PIN requirements
  const [pinBlockTable, setPinBlockTable]               = useState(true);
  const [pinMoveReservation, setPinMoveReservation]     = useState(false);
  const [pinAddTable, setPinAddTable]                   = useState(false);
  const [pinSplitTables, setPinSplitTables]             = useState(false);

  const restaurantRef = doc(db, 'restaurants_data', restaurantId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(restaurantRef);
      if (snap.exists()) {
        const d = snap.data();
        setRestaurantName(d.restaurantName || '');
        setAddress(d.address || '');
        setManagerKey(d.managerKey || '');
        setAllowWebReservations(!!d.allowWebReservations);
        setAllowOnlineOrders(!!d.allowOnlineOrders);
        setAllowReservationOrders(!!d.allowReservationOrders);
        setAllowWaitlistPreOrders(!!d.allowWaitlistPreOrders);
        setAllowSameDayReservations(!!d.allowSameDayReservations);
        setSameDayBufferHours(d.sameDayBufferHours ?? 0);
        setAllowNewGiftCards(!!d.allowNewGiftCards);
        setReservationLength(d.reservationLength || 60);
        setAverageReservation(typeof d.averageReservation === 'number' ? d.averageReservation : 90);
        setAllowReminder(!!d.allowReminder);
        setReminderMinutes(typeof d.reminderMinutes === 'number' ? d.reminderMinutes : 60);
        setAllowAutoDrop(!!d.allowAutoDrop);
        setAutoDropMinutes(typeof d.autoDropMinutes === 'number' ? d.autoDropMinutes : 15);
        setAutoDropMarkNoShow(!!d.autoDropMarkNoShow);
        setConfirmationTextsEnabled(!!d.confirmationTexts?.enabled);
        setConfirmationSendHour(d.confirmationTexts?.sendHour ?? 10);
        const fps = d.floorPinSettings || {};
        setPinBlockTable(fps.requirePinForBlockTable ?? true);
        setPinMoveReservation(!!fps.requirePinForMoveReservation);
        setPinAddTable(!!fps.requirePinForAddTable);
        setPinSplitTables(!!fps.requirePinForSplitTables);
      }
    } catch (err) {
      setError('Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Load avg turn time: seatedTime → bussingSince (last 90 days of completed tables)
  useEffect(() => {
    let active = true;
    const loadAvgTurn = async () => {
      setAvgTurnLoading(true);
      try {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - 90);
        const snap = await getDocs(
          query(
            collection(db, 'restaurants_data', restaurantId, 'reservations'),
            where('date', '>=', Timestamp.fromDate(cutoff)),
          )
        );
        const turns = [];
        snap.docs.forEach(d => {
          const r = d.data();
          const seated = r.seatedTime?.toMillis?.();
          const cleared = r.bussingSince ?? r.completedAt; // Date.now() numbers
          if (seated && cleared && cleared > seated) {
            turns.push((cleared - seated) / 60000);
          }
        });
        if (active && turns.length > 0) {
          setAvgTurnMins(turns.reduce((s, x) => s + x, 0) / turns.length);
        }
      } catch (_) {
        // non-critical — silently skip
      } finally {
        if (active) setAvgTurnLoading(false);
      }
    };
    loadAvgTurn();
    return () => { active = false; };
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (field, value) => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(restaurantRef, { [field]: value });
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.GENERAL_SETTINGS_SAVED, { field, value });
    } catch (err) {
      setError(`Failed to save ${field}.`);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field, setter) => async (value) => {
    setter(value);
    await save(field, value);
  };

  const togglePin = (key, setter) => async (value) => {
    setter(value);
    await save(`floorPinSettings.${key}`, value);
  };

  const handleSaveRestaurantName = async () => {
    if (!restaurantName.trim()) return;
    setSavingName(true);
    setNameSaved(false);
    try {
      await updateDoc(restaurantRef, { restaurantName: restaurantName.trim() });
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.GENERAL_SETTINGS_SAVED, { field: 'restaurantName', value: restaurantName.trim() });
    } catch (err) {
      setError('Failed to update restaurant name.');
    } finally {
      setSavingName(false);
    }
  };

  const handleSaveAddress = async () => {
    setSavingAddress(true);
    setAddressSaved(false);
    try {
      await updateDoc(restaurantRef, { address: address.trim() });
      setAddressSaved(true);
      setTimeout(() => setAddressSaved(false), 3000);
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.GENERAL_SETTINGS_SAVED, { field: 'address', value: address.trim() });
    } catch (err) {
      setError('Failed to update address.');
    } finally {
      setSavingAddress(false);
    }
  };

  const handleSaveManagerKey = async () => {
    if (!newManagerKey.trim()) return;
    setSavingKey(true);
    setKeySaved(false);
    try {
      await updateDoc(restaurantRef, { managerKey: newManagerKey.trim() });
      setManagerKey(newManagerKey.trim());
      setNewManagerKey('');
      setKeySaved(true);
      setTimeout(() => setKeySaved(false), 3000);
    } catch (err) {
      setError('Failed to update manager key.');
    } finally {
      setSavingKey(false);
    }
  };

  const handleReservationLength = async (mins) => {
    setReservationLength(mins);
    await save('reservationLength', mins);
  };

  const handleAverageReservation = async (mins) => {
    const parsed = parseInt(mins, 10);
    if (isNaN(parsed) || parsed < 1) return;
    setAverageReservation(parsed);
    await save('averageReservation', parsed);
  };

  const handleBufferHours = async (hrs) => {
    setSameDayBufferHours(hrs);
    await save('sameDayBufferHours', hrs);
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading settings…</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Restaurant Name */}
      <Card>
        <SectionHeader
          title="Restaurant Name"
          description="Used in SMS messages sent to guests. This is how your restaurant appears in texts."
        />
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={restaurantName}
            onChange={e => setRestaurantName(e.target.value)}
            placeholder="e.g. Spring Restaurant"
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          <button
            onClick={handleSaveRestaurantName}
            disabled={savingName}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
          >
            {nameSaved ? 'Saved!' : savingName ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Card>

      {/* Address */}
      <Card>
        <SectionHeader
          title="Address"
          description="The restaurant's street address. Used in receipts, emails, and schema.org metadata."
        />
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={address}
            onChange={e => setAddress(e.target.value)}
            placeholder="123 Main St, City, State ZIP"
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          <button
            onClick={handleSaveAddress}
            disabled={savingAddress}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
          >
            {addressSaved ? 'Saved!' : savingAddress ? 'Saving…' : 'Save'}
          </button>
        </div>
      </Card>

      {/* Manager Key — commented out, replaced by PIN-based manager auth in the POS
      <Card>
        <SectionHeader
          title="Manager Key"
          description="Required to access restricted POS features. Keep this private."
        />
        <div className="flex items-center gap-2">
          <div className="text-sm text-neutral-500 shrink-0">
            Current: <span className="font-semibold text-neutral-800 font-mono">{managerKey || '—'}</span>
          </div>
          <div className="flex-1" />
          <input
            type="text"
            value={newManagerKey}
            onChange={e => setNewManagerKey(e.target.value)}
            placeholder="New key"
            className="w-36 px-3 py-1.5 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 font-mono"
            onKeyDown={e => e.key === 'Enter' && handleSaveManagerKey()}
          />
          <button
            onClick={handleSaveManagerKey}
            disabled={savingKey || !newManagerKey.trim()}
            className="px-4 py-1.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors"
          >
            {savingKey ? 'Saving…' : 'Update'}
          </button>
          {keySaved && <span className="text-sm text-emerald-600 font-medium">Saved!</span>}
        </div>
      </Card>
      */}

      {/* Reservation settings */}
      <Card>
        <SectionHeader title="Reservations" />

        <SettingRow
          label="Allow Online Reservations"
          description="Customers can book via the restaurant website."
          value={allowWebReservations}
          onToggle={toggle('allowWebReservations', setAllowWebReservations)}
          saving={saving}
        />

        <SettingRow
          label="Allow Same-Day Reservations"
          description="Customers can book for the same day they're visiting."
          value={allowSameDayReservations}
          onToggle={toggle('allowSameDayReservations', setAllowSameDayReservations)}
          saving={saving}
        />

        {allowSameDayReservations && (
          <div className="py-3 border-b border-neutral-100">
            <p className="text-sm font-medium text-neutral-500 mb-2">Same-Day Buffer (hours from present that online reservations can be made)</p>
            <div className="flex gap-2 flex-wrap">
              {[0, 1, 2, 3, 4, 5, 6].map(hr => (
                <button
                  key={hr}
                  onClick={() => handleBufferHours(hr)}
                  className={`w-10 h-10 text-sm font-semibold rounded-lg transition-colors ${sameDayBufferHours === hr ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                >
                  {hr}
                </button>
              ))}
            </div>
          </div>
        )}

        <SettingRow
          label="Allow Reservation Pre-Orders"
          description="Guests can pre-order food when booking. For safety, all food and drink are not made official until party arrives"
          value={allowReservationOrders}
          onToggle={toggle('allowReservationOrders', setAllowReservationOrders)}
          saving={saving}
        />

        <SettingRow
          label="Allow Waitlist Pre-Orders"
          description="Guests added to the waitlist can pre-order food while waiting for a table"
          value={allowWaitlistPreOrders}
          onToggle={toggle('allowWaitlistPreOrders', setAllowWaitlistPreOrders)}
          saving={saving}
        />

        <div className="py-3">
          <p className="text-sm font-medium text-neutral-600 mb-1">Reservation Length (minutes)</p>

          {/* Measured avg turn time */}
          <div className="mb-3 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-lg">
            <p className="text-xs text-neutral-500">
              <span className="font-semibold text-neutral-700">Measured avg turn time (90 days):</span>{' '}
              {avgTurnLoading
                ? 'Loading…'
                : avgTurnMins != null
                  ? (() => {
                      const h = Math.floor(avgTurnMins / 60);
                      const m = Math.round(avgTurnMins % 60);
                      return `${h > 0 ? `${h}h ` : ''}${m}m — seated until table cleared`;
                    })()
                  : 'No data yet — measures from seated time to when table is cleared for bussing'}
            </p>
          </div>

          <div className="flex gap-2 flex-wrap">
            {[45, 60, 75, 90, 105, 120].map(mins => (
              <button
                key={mins}
                onClick={() => handleReservationLength(mins)}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${reservationLength === mins ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
              >
                {mins}
              </button>
            ))}
          </div>
        </div>

        {/* Average Turn Time */}
        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-600 mb-1">Average Turn Time (minutes)</p>
          <p className="text-xs text-neutral-400 mb-2">Used for seating capacity estimates. Default: 90 minutes.</p>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              min={1}
              value={averageReservation}
              onChange={e => setAverageReservation(e.target.value)}
              onBlur={e => handleAverageReservation(e.target.value)}
              className="w-24 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 text-center"
            />
            <span className="text-sm text-neutral-400">minutes</span>
          </div>
        </div>

        {/* ── Reminder SMS ── */}
        <SettingRow
          label="Reminder SMS"
          description="Send a reminder text to guests before their reservation."
          value={allowReminder}
          onToggle={async (v) => { setAllowReminder(v); await save('allowReminder', v); }}
          saving={saving}
        />
        {allowReminder && (
          <div className="py-3 border-b border-neutral-100">
            <p className="text-sm font-medium text-neutral-500 mb-2">Send reminder how many minutes before?</p>
            <div className="flex gap-2 flex-wrap">
              {[30, 60, 90, 120, 180, 240].map(mins => (
                <button
                  key={mins}
                  onClick={async () => { setReminderMinutes(mins); await save('reminderMinutes', mins); }}
                  className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${reminderMinutes === mins ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                >
                  {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Auto-Drop SMS ── */}
        <SettingRow
          label="Auto-Drop SMS"
          description="Automatically notify guests who haven't arrived by a set time after their reservation."
          value={allowAutoDrop}
          onToggle={async (v) => { setAllowAutoDrop(v); await save('allowAutoDrop', v); }}
          saving={saving}
        />
        {allowAutoDrop && (
          <div className="py-3 border-b border-neutral-100 space-y-4">
            <div>
              <p className="text-sm font-medium text-neutral-500 mb-2">Send auto-drop how many minutes after reservation time?</p>
              <div className="flex gap-2 flex-wrap">
                {[10, 15, 20, 30, 45, 60].map(mins => (
                  <button
                    key={mins}
                    onClick={async () => { setAutoDropMinutes(mins); await save('autoDropMinutes', mins); }}
                    className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${autoDropMinutes === mins ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                  >
                    {mins}m
                  </button>
                ))}
              </div>
            </div>
            <SettingRow
              label="Mark as No-Show Automatically"
              description="When the auto-drop SMS fires, also mark the reservation as a no-show. This counts toward enforcement thresholds."
              value={autoDropMarkNoShow}
              onToggle={async (v) => { setAutoDropMarkNoShow(v); await save('autoDropMarkNoShow', v); }}
              saving={saving}
            />
          </div>
        )}
      </Card>

      {/* Day-Before Confirmation Texts */}
      <Card>
        <SectionHeader
          title="Day-Before Confirmation Texts"
          description="Automatically send guests an SMS the day before their reservation asking them to confirm or cancel."
        />
        <SettingRow
          label="Enable Confirmation Texts"
          description="Guests receive an SMS the evening before asking them to reply YES to confirm or CANCEL to cancel."
          value={confirmationTextsEnabled}
          onToggle={async (v) => {
            setConfirmationTextsEnabled(v);
            await save('confirmationTexts', { enabled: v, sendHour: confirmationSendHour });
          }}
          saving={saving}
        />
        {confirmationTextsEnabled && (
          <div className="py-3 border-b border-neutral-100">
            <p className="text-sm font-medium text-neutral-500 mb-2">What hour (UTC) should confirmation texts go out?</p>
            <div className="flex gap-2 flex-wrap">
              {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20].map(h => {
                const label = h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`;
                return (
                  <button
                    key={h}
                    onClick={async () => {
                      setConfirmationSendHour(h);
                      await save('confirmationTexts', { enabled: confirmationTextsEnabled, sendHour: h });
                    }}
                    className={`px-3 py-2 text-sm font-semibold rounded-lg transition-colors ${confirmationSendHour === h ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-neutral-400 mt-2">This is in UTC. Adjust for your local timezone (e.g. 10 UTC = 6 AM ET).</p>
          </div>
        )}
      </Card>

      {/* Online / Website */}
      <Card>
        <SectionHeader title="Online & Website" />

        <SettingRow
          label="Allow Online Pickup Orders"
          description="Customers can place pickup orders from the restaurant website."
          value={allowOnlineOrders}
          onToggle={toggle('allowOnlineOrders', setAllowOnlineOrders)}
          saving={saving}
        />

        <SettingRow
          label="Allow New Gift Cards"
          description="Customers can purchase new gift cards online."
          value={allowNewGiftCards}
          onToggle={toggle('allowNewGiftCards', setAllowNewGiftCards)}
          saving={saving}
        />
      </Card>

      {/* Floor Action PIN Requirements */}
      <Card>
        <SectionHeader
          title="Floor Action PIN Requirements"
          description="When enabled, a manager or owner must authorize these actions on the POS floor map."
        />

        <SettingRow
          label="Block / Unblock Table"
          description="Taking a table offline requires manager authorization."
          value={pinBlockTable}
          onToggle={togglePin('requirePinForBlockTable', setPinBlockTable)}
          saving={saving}
        />

        <SettingRow
          label="Move Reservation"
          description="Reassigning a reservation to a different table requires manager authorization."
          value={pinMoveReservation}
          onToggle={togglePin('requirePinForMoveReservation', setPinMoveReservation)}
          saving={saving}
        />

        <SettingRow
          label="Add Table"
          description="Combining an additional table into an existing reservation requires manager authorization."
          value={pinAddTable}
          onToggle={togglePin('requirePinForAddTable', setPinAddTable)}
          saving={saving}
        />

        <SettingRow
          label="Split Tables"
          description="Removing a table from a combined reservation requires manager authorization."
          value={pinSplitTables}
          onToggle={togglePin('requirePinForSplitTables', setPinSplitTables)}
          saving={saving}
        />
      </Card>


    </div>
  );
};

// ── Enforcement Tab ───────────────────────────────────────────────────────────

const EnforcementTab = ({ restaurantId, user }) => {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const [autoBlockEnabled, setAutoBlockEnabled]                     = useState(true);
  const [lateCancelWindowHours, setLateCancelWindowHours]           = useState(6);
  const [toleratedCancellations, setToleratedCancellations]         = useState(3);
  const [toleratedLateCancellations, setToleratedLateCancellations] = useState(2);
  const [toleratedNoShows, setToleratedNoShows]                     = useState(2);
  const [toleratedWalkOuts, setToleratedWalkOuts]                   = useState(1);

  const [blockedNumbers, setBlockedNumbers]   = useState([]);
  const [atRiskNumbers, setAtRiskNumbers]     = useState([]);
  const [numbersLoading, setNumbersLoading]   = useState(false);

  const [manualPhone, setManualPhone]         = useState('');
  const [manualReason, setManualReason]       = useState('');
  const [manualBlocking, setManualBlocking]   = useState(false);

  const [lookupPhone, setLookupPhone]         = useState('');
  const [lookupResult, setLookupResult]       = useState(null);
  const [lookupLoading, setLookupLoading]     = useState(false);
  const [lookupSearched, setLookupSearched]   = useState(false);

  const restaurantRef = doc(db, 'restaurants_data', restaurantId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(restaurantRef);
      if (snap.exists()) {
        const es = snap.data().enforcementSettings || {};
        setAutoBlockEnabled(es.autoBlockEnabled ?? true);
        setLateCancelWindowHours(es.lateCancelWindowHours ?? 6);
        setToleratedCancellations(es.toleratedCancellations ?? 3);
        setToleratedLateCancellations(es.toleratedLateCancellations ?? 2);
        setToleratedNoShows(es.toleratedNoShows ?? 2);
        setToleratedWalkOuts(es.toleratedWalkOuts ?? 1);
      }
    } catch { setError('Failed to load enforcement settings.'); }
    finally { setLoading(false); }
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const loadNumbers = useCallback(async () => {
    setNumbersLoading(true);
    try {
      const snap = await getDocs(collection(db, 'restaurants_data', restaurantId, 'blockedNumbers'));
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Consider a number blocked if blockedAt is a non-null Timestamp OR if a legacy `blocked: true` field exists
      setBlockedNumbers(all.filter(d => d.blockedAt != null || d.blocked === true));
      setAtRiskNumbers(all.filter(d =>
        !d.blockedAt && !d.blocked &&
        ((d.totalCancellations || 0) > 0 || (d.noShows || 0) > 0 || (d.walkOuts || 0) > 0)
      ));
    } catch (err) {
      if (err?.code === 'permission-denied') {
        window.dispatchEvent(new Event('gohost:session-expired'));
        return;
      }
      setError('Failed to load phone numbers — ' + (err?.message || 'unknown error'));
    }
    finally { setNumbersLoading(false); }
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadNumbers(); }, [loadNumbers]);

  const saveEnforcement = async (key, setter, value) => {
    setter(value);
    setSaving(true);
    try {
      await updateDoc(restaurantRef, { [`enforcementSettings.${key}`]: value });
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.ENFORCEMENT_SAVED, { field: key, value });
    } catch { setError(`Failed to save ${key}.`); }
    finally { setSaving(false); }
  };

  const handleLookup = async () => {
    const phone = lookupPhone.trim();
    if (!phone) return;
    setLookupLoading(true);
    setLookupSearched(true);
    try {
      const snap = await getDoc(doc(db, 'restaurants_data', restaurantId, 'blockedNumbers', phone));
      setLookupResult(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    } catch { setLookupResult(null); }
    finally { setLookupLoading(false); }
  };

  const handleManualBlock = async (phone, reason) => {
    const p = (phone || '').trim();
    if (!p) return;
    setManualBlocking(true);
    try {
      await setDoc(doc(db, 'restaurants_data', restaurantId, 'blockedNumbers', p), {
        phoneNumber: p,
        blockedAt: serverTimestamp(),
        blockedBy: 'manual',
        reason: (reason || '').trim() || 'Manually blocked by manager.',
        isManual: true,
        totalCancellations: 0,
        lateCancellations: 0,
        noShows: 0,
        walkOuts: 0,
        lastIncidentAt: serverTimestamp(),
      }, { merge: true });
      setManualPhone('');
      setManualReason('');
      if (lookupPhone.trim() === p) {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId, 'blockedNumbers', p));
        setLookupResult(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      }
      await loadNumbers();
    } catch { setError('Failed to block number.'); }
    finally { setManualBlocking(false); }
  };

  const handleUnblock = async (phoneNumber) => {
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId, 'blockedNumbers', phoneNumber), {
        blockedAt: null,
        reason: null,
        blockedBy: null,
      });
      if (lookupResult?.id === phoneNumber) {
        setLookupResult(prev => ({ ...prev, blockedAt: null, reason: null, blockedBy: null }));
      }
      await loadNumbers();
    } catch { setError('Failed to unblock number.'); }
  };

  const handleClearHistory = async (phoneNumber) => {
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId, 'blockedNumbers', phoneNumber), {
        blockedAt: null,
        reason: null,
        blockedBy: null,
        totalCancellations: 0,
        lateCancellations: 0,
        noShows: 0,
        walkOuts: 0,
        lastIncidentAt: null,
      });
      if (lookupResult?.id === phoneNumber) {
        setLookupResult(prev => ({
          ...prev, blockedAt: null, reason: null, blockedBy: null,
          totalCancellations: 0, lateCancellations: 0, noShows: 0, walkOuts: 0, lastIncidentAt: null,
        }));
      }
      await loadNumbers();
    } catch { setError('Failed to clear history.'); }
  };

  const fmtDate = (ts) => ts?.toDate
    ? ts.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';

  const IncidentStats = ({ record }) => {
    const items = [
      record.totalCancellations > 0 && `${record.totalCancellations} cancel${record.totalCancellations !== 1 ? 's' : ''}`,
      record.lateCancellations   > 0 && `${record.lateCancellations} late cancel${record.lateCancellations !== 1 ? 's' : ''}`,
      record.noShows             > 0 && `${record.noShows} no-show${record.noShows !== 1 ? 's' : ''}`,
      record.walkOuts            > 0 && `${record.walkOuts} walk-out${record.walkOuts !== 1 ? 's' : ''}`,
    ].filter(Boolean);
    if (!items.length) return <p className="text-xs text-neutral-400">No tracked incidents.</p>;
    return (
      <div className="flex gap-3 text-xs text-neutral-500 flex-wrap">
        {items.map(s => <span key={s}>{s}</span>)}
      </div>
    );
  };

  const ThresholdBar = ({ label, current, max }) => {
    if (max === 0) return null;
    const at = current >= max;
    const pct = Math.min((current / (max + 1)) * 100, 100);
    return (
      <div className="flex items-center gap-2 text-xs">
        <span className="text-neutral-400 w-24 shrink-0">{label}</span>
        <div className="flex-1 bg-neutral-100 rounded-full h-1.5 max-w-[80px]">
          <div className={`h-1.5 rounded-full transition-all ${at ? 'bg-red-400' : 'bg-amber-400'}`} style={{ width: `${pct}%` }} />
        </div>
        <span className={`font-mono tabular-nums ${at ? 'text-red-500 font-semibold' : 'text-neutral-500'}`}>{current}/{max + 1}</span>
      </div>
    );
  };

  const ThresholdBtn = ({ options, value, onSave, width = 'w-10' }) => (
    <div className="flex gap-2 flex-wrap">
      {options.map(n => (
        <button key={n} onClick={() => onSave(n)} disabled={saving}
          className={`${width} h-9 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${value === n ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
          {n}
        </button>
      ))}
    </div>
  );

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading…</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      {error && <p className="text-sm text-red-600 mb-2">{error}</p>}

      {/* ── Auto-Block Settings ── */}
      <Card>
        <SectionHeader
          title="Auto-Block Settings"
          description="Phone numbers that exceed these thresholds are automatically blocked from online reservations. Incidents are always tracked even when auto-block is off."
        />

        <SettingRow
          label="Auto-Block Enabled"
          description="When off, incidents are still tracked but numbers are never blocked automatically."
          value={autoBlockEnabled}
          onToggle={v => saveEnforcement('autoBlockEnabled', setAutoBlockEnabled, v)}
          saving={saving}
        />

        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-600 mb-1">Late Cancellation Window</p>
          <p className="text-xs text-neutral-400 mb-2">A cancellation is "late" if made within this many hours of the reservation. 0 = disabled.</p>
          <div className="flex gap-2 flex-wrap">
            {[0, 1, 2, 4, 6, 12, 24].map(h => (
              <button key={h} onClick={() => saveEnforcement('lateCancelWindowHours', setLateCancelWindowHours, h)} disabled={saving}
                className={`w-12 h-9 text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${lateCancelWindowHours === h ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'}`}>
                {h === 0 ? 'Off' : `${h}h`}
              </button>
            ))}
          </div>
        </div>

        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-600 mb-1">Tolerated Cancellations</p>
          <p className="text-xs text-neutral-400 mb-2">Block after exceeding this many total cancellations.</p>
          <ThresholdBtn options={[0, 1, 2, 3, 4, 5, 10]} value={toleratedCancellations} onSave={v => saveEnforcement('toleratedCancellations', setToleratedCancellations, v)} />
        </div>

        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-600 mb-1">Tolerated Late Cancellations</p>
          <p className="text-xs text-neutral-400 mb-2">Block after exceeding this many late cancellations.</p>
          <ThresholdBtn options={[0, 1, 2, 3, 4, 5]} value={toleratedLateCancellations} onSave={v => saveEnforcement('toleratedLateCancellations', setToleratedLateCancellations, v)} />
        </div>

        <div className="py-3 border-b border-neutral-100">
          <p className="text-sm font-medium text-neutral-600 mb-1">Tolerated No-Shows</p>
          <p className="text-xs text-neutral-400 mb-2">Block after exceeding this many no-shows.</p>
          <ThresholdBtn options={[0, 1, 2, 3, 4, 5]} value={toleratedNoShows} onSave={v => saveEnforcement('toleratedNoShows', setToleratedNoShows, v)} />
        </div>

        <div className="py-3">
          <p className="text-sm font-medium text-neutral-600 mb-1">Tolerated Walk-Outs</p>
          <p className="text-xs text-neutral-400 mb-2">Block after exceeding this many dine-and-dash / walk-out incidents.</p>
          <ThresholdBtn options={[0, 1, 2, 3]} value={toleratedWalkOuts} onSave={v => saveEnforcement('toleratedWalkOuts', setToleratedWalkOuts, v)} />
        </div>
      </Card>

      {/* ── Phone Number Lookup ── */}
      <Card>
        <SectionHeader
          title="Phone Number Lookup"
          description="Look up any phone number to see their reservation incident history at this restaurant."
        />
        <div className="flex gap-2">
          <input
            type="tel"
            value={lookupPhone}
            onChange={e => { setLookupPhone(e.target.value); setLookupSearched(false); setLookupResult(null); }}
            onKeyDown={e => e.key === 'Enter' && handleLookup()}
            placeholder="+15550001234"
            className="flex-1 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 font-mono"
          />
          <button
            onClick={handleLookup}
            disabled={lookupLoading || !lookupPhone.trim()}
            className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors shrink-0"
          >
            {lookupLoading ? 'Looking up…' : 'Look Up'}
          </button>
        </div>

        {lookupSearched && (
          <div className="mt-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
            {!lookupResult ? (
              <p className="text-sm text-neutral-500">No record found — this number has no tracked incidents at this restaurant.</p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold font-mono text-neutral-800">{lookupResult.phoneNumber || lookupResult.id}</span>
                  {lookupResult.blockedAt ? (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-600">
                      {lookupResult.isManual ? 'Manually Blocked' : 'Auto-Blocked'}
                    </span>
                  ) : (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">Not Blocked</span>
                  )}
                </div>
                {lookupResult.reason && <p className="text-xs text-neutral-500">{lookupResult.reason}</p>}
                <IncidentStats record={lookupResult} />
                {lookupResult.lastIncidentAt && (
                  <p className="text-xs text-neutral-400">Last incident: {fmtDate(lookupResult.lastIncidentAt)}</p>
                )}
                <div className="flex gap-2 pt-1 flex-wrap">
                  {lookupResult.blockedAt ? (
                    <button onClick={() => handleUnblock(lookupResult.id)}
                      className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                      Unblock
                    </button>
                  ) : (
                    <button onClick={() => handleManualBlock(lookupResult.id, '')}
                      className="px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
                      Block Number
                    </button>
                  )}
                  <button onClick={() => handleClearHistory(lookupResult.id)}
                    className="px-3 py-1 text-xs font-semibold text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors">
                    Clear History
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ── Block a Number ── */}
      <Card>
        <SectionHeader
          title="Block a Number"
          description="Manually block a phone number from making online reservations."
        />
        <div className="flex gap-2 flex-wrap">
          <input
            type="tel"
            value={manualPhone}
            onChange={e => setManualPhone(e.target.value)}
            placeholder="+15550001234"
            className="w-44 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 font-mono"
          />
          <input
            type="text"
            value={manualReason}
            onChange={e => setManualReason(e.target.value)}
            placeholder="Reason (optional)"
            className="flex-1 min-w-36 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
          />
          <button
            onClick={() => handleManualBlock(manualPhone, manualReason)}
            disabled={manualBlocking || !manualPhone.trim()}
            className="px-4 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors shrink-0"
          >
            {manualBlocking ? 'Blocking…' : 'Block Number'}
          </button>
        </div>
      </Card>

      {/* ── At-Risk Numbers ── */}
      {atRiskNumbers.length > 0 && (
        <Card>
          <SectionHeader
            title={`At-Risk Numbers (${atRiskNumbers.length})`}
            description="These numbers have incidents on record but haven't reached the auto-block threshold yet."
          />
          <div className="divide-y divide-neutral-100">
            {atRiskNumbers.map(r => (
              <div key={r.id} className="py-3 flex items-start justify-between gap-3">
                <div className="space-y-1.5 min-w-0 flex-1">
                  <p className="text-sm font-semibold font-mono text-neutral-800">{r.phoneNumber || r.id}</p>
                  <div className="space-y-1">
                    <ThresholdBar label="Cancellations" current={r.totalCancellations || 0} max={toleratedCancellations} />
                    {lateCancelWindowHours > 0 && (
                      <ThresholdBar label="Late Cancels" current={r.lateCancellations || 0} max={toleratedLateCancellations} />
                    )}
                    <ThresholdBar label="No-Shows" current={r.noShows || 0} max={toleratedNoShows} />
                    {toleratedWalkOuts > 0 && (
                      <ThresholdBar label="Walk-Outs" current={r.walkOuts || 0} max={toleratedWalkOuts} />
                    )}
                  </div>
                  {r.lastIncidentAt && (
                    <p className="text-xs text-neutral-400">Last incident: {fmtDate(r.lastIncidentAt)}</p>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => handleManualBlock(r.id, '')}
                    className="px-3 py-1 text-xs font-semibold text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors">
                    Block
                  </button>
                  <button onClick={() => handleClearHistory(r.id)}
                    className="px-3 py-1 text-xs font-semibold text-neutral-500 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors">
                    Clear
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Blocked Numbers ── */}
      <Card>
        <SectionHeader
          title={`Blocked Numbers${blockedNumbers.length > 0 ? ` (${blockedNumbers.length})` : ''}`}
          description="Phone numbers currently blocked from making online reservations."
        />
        {numbersLoading ? (
          <p className="text-sm text-neutral-400 py-4 text-center">Loading…</p>
        ) : blockedNumbers.length === 0 ? (
          <p className="text-sm text-neutral-400 py-4 text-center">No blocked numbers.</p>
        ) : (
          <div className="divide-y divide-neutral-100">
            {blockedNumbers.map(b => (
              <div key={b.id} className="py-3 flex items-start justify-between gap-3">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold font-mono text-neutral-800">{b.phoneNumber || b.id}</span>
                    <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${b.isManual ? 'bg-neutral-100 text-neutral-500' : 'bg-red-50 text-red-600'}`}>
                      {b.isManual ? 'Manual' : 'Auto-blocked'}
                    </span>
                  </div>
                  {b.reason && <p className="text-xs text-neutral-500">{b.reason}</p>}
                  <IncidentStats record={b} />
                  <div className="flex gap-3 text-xs text-neutral-400 flex-wrap">
                    <span>Blocked: {fmtDate(b.blockedAt)}</span>
                    <span>Last incident: {fmtDate(b.lastIncidentAt)}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => handleUnblock(b.id)}
                    className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                    Unblock
                  </button>
                  <button onClick={() => handleClearHistory(b.id)}
                    className="px-3 py-1 text-xs font-semibold text-neutral-500 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors">
                    Clear All
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

// ── Messaging Tab ─────────────────────────────────────────────────────────────

const MessagingTab = ({ restaurantId, user, onDirtyChange }) => {
  const [defaultMessages, setDefaultMessages] = useState({});
  const [messageSettings, setMessageSettings] = useState({});
  const [loading, setLoading]                 = useState(true);
  const [saving, setSaving]                   = useState(null); // id of saving msg
  const [error, setError]                     = useState('');

  // Local custom text per message type
  const [customText, setCustomText] = useState({});

  // Track which message IDs have unsaved custom-text edits
  const [dirtyIds, setDirtyIds] = useState(new Set());
  const saveAllRef = useRef(null);

  useEffect(() => {
    const loadMessages = async () => {
      setLoading(true);
      try {
        // Load default messages from global sms_messages collection
        const defaults = {};
        for (const msg of MSG_TYPES) {
          try {
            const snap = await getDoc(doc(db, 'sms_messages', msg.id));
            if (snap.exists()) defaults[msg.id] = snap.data().text || '';
          } catch { /* global collection may have restricted rules — skip */ }
        }
        setDefaultMessages(defaults);

        // Load restaurant-specific overrides
        const settings = {};
        const texts = {};
        for (const msg of MSG_TYPES) {
          try {
            const snap = await getDoc(doc(db, 'restaurants_data', restaurantId, 'messages', msg.id));
            if (snap.exists()) {
              const d = snap.data();
              settings[msg.id] = d.mode || 'default';
              texts[msg.id] = d.customMessage || '';
            } else {
              settings[msg.id] = 'default';
              texts[msg.id] = '';
            }
          } catch {
            settings[msg.id] = 'default';
            texts[msg.id] = '';
          }
        }
        setMessageSettings(settings);
        setCustomText(texts);
      } catch (err) {
        setError('Failed to load messaging settings.');
      } finally {
        setLoading(false);
      }
    };
    loadMessages();
  }, [restaurantId]);

  const saveMessage = async (id, mode, text) => {
    setSaving(id);
    setError('');
    try {
      await updateDoc(
        doc(db, 'restaurants_data', restaurantId, 'messages', id),
        { mode, customMessage: text || '' }
      );
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.MESSAGING_SETTINGS_SAVED, { templateType: id, mode });
    } catch {
      // doc may not exist yet — use setDoc
      const { setDoc } = await import('firebase/firestore');
      await setDoc(
        doc(db, 'restaurants_data', restaurantId, 'messages', id),
        { mode, customMessage: text || '' },
        { merge: true }
      );
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.MESSAGING_SETTINGS_SAVED, { templateType: id, mode });
    } finally {
      setSaving(null);
      // Mark this message as clean
      setDirtyIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        if (next.size === 0) onDirtyChange?.(false, null);
        return next;
      });
    }
  };

  const setMode = async (id, mode) => {
    setMessageSettings(prev => ({ ...prev, [id]: mode }));
    if (mode === 'default') {
      // Switching to default discards custom text — clear dirty for this id
      setDirtyIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        if (next.size === 0) onDirtyChange?.(false, null);
        return next;
      });
      await saveMessage(id, 'default', '');
    }
  };

  const insertToken = (id, token) => {
    setCustomText(prev => ({ ...prev, [id]: (prev[id] || '') + token }));
    markMsgDirty(id);
  };

  const markMsgDirty = (id) => {
    setDirtyIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // Whenever dirtyIds changes, notify parent (via ref so we can call saveAll)
  useEffect(() => {
    if (dirtyIds.size === 0) {
      saveAllRef.current = null;
      onDirtyChange?.(false, null);
    } else {
      saveAllRef.current = async () => {
        for (const id of dirtyIds) {
          await saveMessage(id, 'custom', customText[id] || '');
        }
      };
      onDirtyChange?.(true, () => saveAllRef.current?.());
    }
  }, [dirtyIds]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading messaging settings…</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <p className="text-sm text-neutral-400">
        Set each message to use the GoHost default or write a custom template. Use token chips to insert dynamic values.
      </p>

      {MSG_TYPES.map(msg => {
        const mode = messageSettings[msg.id] || 'default';
        const text = customText[msg.id] || '';
        const isSaving = saving === msg.id;

        return (
          <Card key={msg.id}>
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-neutral-800">{msg.label}</p>
              <div className="flex gap-1.5">
                {['default', 'custom'].map(m => (
                  <button
                    key={m}
                    onClick={() => setMode(msg.id, m)}
                    className={`px-3 py-1 text-sm font-semibold rounded-lg capitalize transition-colors ${mode === m ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {/* Default preview */}
            {mode === 'default' && (
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2.5">
                <p className="text-sm font-medium text-neutral-400 mb-1">Default message</p>
                <p className="text-sm text-neutral-600 leading-relaxed">
                  {defaultMessages[msg.id] || <span className="italic text-neutral-300">No default message on file.</span>}
                </p>
              </div>
            )}

            {/* Custom editor */}
            {mode === 'custom' && (
              <div className="space-y-2">
                {/* Token chips */}
                <div className="flex flex-wrap gap-1.5">
                  {(MSG_TYPE_TOKENS[msg.id] || []).map(t => (
                    <button
                      key={t.token}
                      onClick={() => insertToken(msg.id, t.token)}
                      className="text-sm px-2 py-1 bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded-md transition-colors font-medium"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <textarea
                  value={text}
                  onChange={e => { setCustomText(prev => ({ ...prev, [msg.id]: e.target.value })); markMsgDirty(msg.id); }}
                  placeholder="Enter custom SMS message…"
                  rows={3}
                  className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 resize-none"
                />

                <button
                  onClick={() => saveMessage(msg.id, 'custom', text)}
                  disabled={isSaving}
                  className="px-4 py-1.5 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                >
                  {isSaving ? 'Saving…' : 'Save Custom Message'}
                </button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

// ── Stripe Tab ────────────────────────────────────────────────────────────────

const StripeTab = ({ restaurantId, user }) => {
  const [stripeAccountId, setStripeAccountId] = useState(null);
  const [loading, setLoading]                 = useState(true);
  const [opening, setOpening]                 = useState(false);
  const [connecting, setConnecting]           = useState(false);
  const [error, setError]                     = useState('');

  const load = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
      if (snap.exists()) setStripeAccountId(snap.data().stripeAccountId || null);
    } catch {
      setError('Failed to load Stripe status.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]);

  useEffect(() => { load(); }, [load]);

  // Re-check when returning from Stripe onboarding (stripe=done param)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'done') load();
  }, [load]);

  const openDashboard = async () => {
    if (!stripeAccountId) return;
    setOpening(true);
    setError('');
    try {
      const result = await createStripeLoginLink({ stripeAccountId });
      window.open(result.data.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError('Could not open Stripe dashboard. Try again.');
    } finally {
      setOpening(false);
    }
  };

  const connectStripe = async () => {
    setConnecting(true);
    setError('');
    try {
      const result = await createStripeRestaurantFn({ restaurantId });
      window.open(result.data.onboardingUrl, '_blank', 'noopener,noreferrer');
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.STRIPE_CONNECTED, {});
    } catch (err) {
      setError('Could not start Stripe onboarding. Try again.');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading…</p>;

  return (
    <div className="max-w-2xl space-y-4">
      <Card>
        <SectionHeader
          title="Stripe Payments"
          description="Stripe is used for reservation deposits, gift cards, and payment processing."
        />

        {stripeAccountId ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
              <p className="text-sm font-medium text-neutral-700">Stripe account connected</p>
            </div>
            <p className="text-sm text-neutral-400 font-mono">{stripeAccountId}</p>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={openDashboard}
                disabled={opening}
                className="px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: '#635bff' }}
              >
                {opening ? 'Opening…' : 'Open Stripe Dashboard'}
              </button>
              <button
                onClick={connectStripe}
                disabled={connecting}
                className="px-5 py-2 text-sm font-semibold rounded-lg border transition-colors disabled:opacity-50"
                style={{ borderColor: '#635bff', color: '#635bff' }}
              >
                {connecting ? 'Opening…' : 'Continue / Re-verify Onboarding'}
              </button>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-neutral-300" />
              <p className="text-sm font-medium text-neutral-500">No Stripe account connected</p>
            </div>
            <p className="text-sm text-neutral-400 leading-relaxed">
              Connect a Stripe account to accept reservation deposits, gift card payments, and in-person card transactions.
            </p>
            <button
              onClick={connectStripe}
              disabled={connecting}
              className="px-5 py-2 text-sm font-semibold rounded-lg text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: '#635bff' }}
            >
              {connecting ? 'Opening Stripe…' : 'Connect Stripe Account'}
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}
      </Card>
    </div>
  );
};

// ── Pickup Tab ────────────────────────────────────────────────────────────────

const PREP_TIME_OPTIONS = [5, 10, 15, 20, 25, 30, 45, 60];

const PickupTab = ({ restaurantId, user }) => {
  const [loading, setLoading]   = useState(true);
  const [saving,  setSaving]    = useState(false);
  const [error,   setError]     = useState('');

  const [settings, setSettings] = useState({
    acceptanceMode:           'manual',
    defaultPrepMinutes:       15,
    requirePhone:             false,
    pickupInstructions:       '',
    allowScheduledPickup:     false,
    allowCashAtPickup:        true,
    sendEtaOnAccept:          true,
    autoDeclineMinutes:       0,
    thresholdApprovalEnabled: false,
    thresholdAmountDollars:   50,
    thresholdItemCount:       0,
  });

  const restaurantRef = doc(db, 'restaurants_data', restaurantId);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(restaurantRef);
        if (snap.exists()) {
          const d = snap.data();
          if (d.pickupSettings) setSettings(s => ({ ...s, ...d.pickupSettings }));
          // also read top-level allowOnlineOrders for context
        }
      } catch { setError('Failed to load pickup settings.'); }
      finally   { setLoading(false); }
    };
    load();
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setSaving(true);
    setError('');
    const merged = { ...settings, ...patch };
    setSettings(merged);
    try {
      await updateDoc(restaurantRef, { pickupSettings: merged });
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.PICKUP_SETTINGS_SAVED, { changedFields: Object.keys(patch) });
    } catch { setError('Failed to save.'); }
    finally   { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading…</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      {error && <p className="text-sm text-red-600">{error}</p>}

      {/* Acceptance */}
      <Card>
        <SectionHeader
          title="Order Acceptance"
          description="Control how incoming pickup orders are handled when they arrive."
        />
        <div className="py-3">
          <p className="text-sm font-medium text-neutral-700 mb-3">Acceptance Mode</p>
          <div className="flex gap-3">
            {[
              { value: 'manual', label: 'Manual', desc: 'Staff taps Accept for each order' },
              { value: 'auto',   label: 'Auto-Accept', desc: 'Orders go straight to kitchen' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => save({ acceptanceMode: opt.value })}
                disabled={saving}
                className={`flex-1 p-4 rounded-xl border-2 text-left transition-colors ${
                  settings.acceptanceMode === opt.value
                    ? 'border-neutral-900 bg-neutral-50'
                    : 'border-neutral-200 hover:border-neutral-400'
                }`}
              >
                <p className="text-sm font-semibold text-neutral-800">{opt.label}</p>
                <p className="text-xs text-neutral-400 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="py-3 border-t border-neutral-100">
          <p className="text-sm font-medium text-neutral-700 mb-2">Default Prep Time</p>
          <p className="text-xs text-neutral-400 mb-3">Shown to customer as their estimated ready time after acceptance.</p>
          <div className="flex gap-2 flex-wrap">
            {PREP_TIME_OPTIONS.map(m => (
              <button
                key={m}
                onClick={() => save({ defaultPrepMinutes: m })}
                disabled={saving}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
                  settings.defaultPrepMinutes === m
                    ? 'bg-neutral-900 text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {m} min
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* Customer requirements */}
      <Card>
        <SectionHeader title="Customer Requirements" />
        <SettingRow
          label="Require Phone Number"
          description="Online orders must include a phone number. Enables SMS notifications when order is ready."
          value={settings.requirePhone}
          onToggle={v => save({ requirePhone: v })}
          saving={saving}
        />
        <SettingRow
          label="Allow Scheduled Pickup"
          description="Customers can choose a future pickup time instead of ASAP."
          value={settings.allowScheduledPickup}
          onToggle={v => save({ allowScheduledPickup: v })}
          saving={saving}
        />
        <SettingRow
          label="Allow Cash at Pickup"
          description="Staff-entered orders can be marked as cash payment (paid at pickup)."
          value={settings.allowCashAtPickup}
          onToggle={v => save({ allowCashAtPickup: v })}
          saving={saving}
        />
      </Card>

      {/* Pickup instructions */}
      <Card>
        <SectionHeader
          title="Pickup Instructions"
          description="Shown to the customer after they place an order. E.g. 'Come to the front counter and give your name.'"
        />
        <textarea
          value={settings.pickupInstructions}
          onChange={e => setSettings(s => ({ ...s, pickupInstructions: e.target.value }))}
          onBlur={() => save({ pickupInstructions: settings.pickupInstructions })}
          placeholder="e.g. Come to the host stand and give your name. We'll have your order ready!"
          rows={3}
          className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300 resize-none"
        />
        <p className="text-xs text-neutral-400 mt-1">Auto-saves on blur.</p>
      </Card>

      {/* ETA Notifications */}
      <Card>
        <SectionHeader
          title="ETA Notifications"
          description="When staff accepts an order, the customer receives an SMS with their estimated ready time."
        />
        <SettingRow
          label="Send ETA on Accept"
          description="Text the customer their estimated ready time the moment an order is accepted."
          value={settings.sendEtaOnAccept}
          onToggle={v => save({ sendEtaOnAccept: v })}
          saving={saving}
        />
      </Card>

      {/* Approval Threshold */}
      <Card>
        <SectionHeader
          title="Large Order Approval"
          description="Require manual staff review for orders above a dollar amount or item count — even in Auto-Accept mode."
        />
        <SettingRow
          label="Enable Threshold Approval"
          description="Large orders will be held as 'new' for staff to manually accept regardless of acceptance mode."
          value={settings.thresholdApprovalEnabled}
          onToggle={v => save({ thresholdApprovalEnabled: v })}
          saving={saving}
        />
        {settings.thresholdApprovalEnabled && (
          <div className="py-3 border-t border-neutral-100 space-y-4">
            <div>
              <p className="text-sm font-medium text-neutral-700 mb-1">Dollar Threshold</p>
              <p className="text-xs text-neutral-400 mb-2">Orders at or above this total require manual approval. Set to 0 to disable dollar check.</p>
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={settings.thresholdAmountDollars}
                  onChange={e => setSettings(s => ({ ...s, thresholdAmountDollars: Number(e.target.value) }))}
                  onBlur={() => save({ thresholdAmountDollars: settings.thresholdAmountDollars })}
                  className="w-24 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-neutral-700 mb-1">Item Count Threshold</p>
              <p className="text-xs text-neutral-400 mb-2">Orders with this many items or more require manual approval. Set to 0 to disable item check.</p>
              <input
                type="number"
                min="0"
                step="1"
                value={settings.thresholdItemCount}
                onChange={e => setSettings(s => ({ ...s, thresholdItemCount: Number(e.target.value) }))}
                onBlur={() => save({ thresholdItemCount: settings.thresholdItemCount })}
                className="w-24 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Auto-Decline */}
      <Card>
        <SectionHeader
          title="Auto-Decline Timeout"
          description="Automatically cancel unaccepted orders after a set number of minutes and notify the customer via SMS. Set to 0 to disable."
        />
        <div className="py-3">
          <p className="text-sm font-medium text-neutral-700 mb-1">Timeout (minutes)</p>
          <p className="text-xs text-neutral-400 mb-2">
            Orders still in "new" status after this many minutes will be cancelled automatically.
            Set to <strong>0</strong> to never auto-decline.
          </p>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              step="1"
              value={settings.autoDeclineMinutes}
              onChange={e => setSettings(s => ({ ...s, autoDeclineMinutes: Number(e.target.value) }))}
              onBlur={() => save({ autoDeclineMinutes: settings.autoDeclineMinutes })}
              className="w-24 px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
            <span className="text-sm text-neutral-500">minutes</span>
          </div>
          {settings.autoDeclineMinutes > 0 && (
            <p className="text-xs text-amber-600 mt-2 font-medium">
              ⚠ Orders not accepted within {settings.autoDeclineMinutes} min will be cancelled and the customer notified.
            </p>
          )}
        </div>
      </Card>

      {/* Future delivery partners notice */}
      <div className="border border-dashed border-neutral-200 rounded-xl p-4 text-center">
        <p className="text-sm font-medium text-neutral-400">🚗 Delivery Partner Integration</p>
        <p className="text-xs text-neutral-400 mt-1">
          Uber Eats, DoorDash, and Grubhub integration is coming in a future update.
          Orders from delivery partners will appear alongside pickup orders and route to the same KDS view.
        </p>
      </div>
    </div>
  );
};

// ── Courses Tab ───────────────────────────────────────────────────────────────

const COURSE_COLORS = [
  '#f59e0b', '#3b82f6', '#a855f7', '#10b981',
  '#ef4444', '#ec4899', '#6366f1', '#64748b',
];

const DEFAULT_COURSES = [
  { id: 'appetizers', label: 'Appetizers', color: '#f59e0b', autoFire: true  },
  { id: 'entrees',    label: 'Entrées',    color: '#3b82f6', autoFire: false },
  { id: 'desserts',   label: 'Desserts',   color: '#a855f7', autoFire: false },
];

const CoursesTab = ({ restaurantId, user }) => {
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [error, setError]               = useState('');
  const [coursesEnabled, setCoursesEnabled] = useState(false);
  const [courses, setCourses]           = useState([]);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [addingLabel, setAddingLabel]   = useState('');
  const [addingColor, setAddingColor]   = useState(COURSE_COLORS[0]);
  const [addingAutoFire, setAddingAutoFire] = useState(false);
  const [addingDelayMinutes, setAddingDelayMinutes] = useState(0);

  const restaurantRef = doc(db, 'restaurants_data', restaurantId);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(restaurantRef);
      if (snap.exists()) {
        const d = snap.data();
        setCoursesEnabled(!!d.coursesEnabled);
        setCourses(Array.isArray(d.courses) ? d.courses : []);
      }
    } catch {
      setError('Failed to load course settings.');
    } finally {
      setLoading(false);
    }
  }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const persist = async (patch) => {
    setSaving(true);
    setError('');
    try {
      await updateDoc(restaurantRef, patch);
      writePortalAuditEvent(restaurantId, user, PORTAL_AUDIT.COURSES_SAVED, { changedFields: Object.keys(patch) });
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (val) => {
    setCoursesEnabled(val);
    if (val && courses.length === 0) {
      // Seed defaults on first enable
      setCourses(DEFAULT_COURSES);
      await persist({ coursesEnabled: true, courses: DEFAULT_COURSES });
    } else {
      await persist({ coursesEnabled: val });
    }
  };

  const saveCourses = async (updated) => {
    setCourses(updated);
    await persist({ courses: updated });
  };

  const handleMoveUp = (i) => {
    if (i === 0) return;
    const next = [...courses];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    saveCourses(next);
  };

  const handleMoveDown = (i) => {
    if (i === courses.length - 1) return;
    const next = [...courses];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    saveCourses(next);
  };

  const handleRemove = (i) => saveCourses(courses.filter((_, idx) => idx !== i));

  const handleUpdate = (i, field, value) =>
    saveCourses(courses.map((c, idx) => idx === i ? { ...c, [field]: value } : c));

  const handleAdd = async () => {
    const label = addingLabel.trim();
    if (!label) return;
    const id = label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    if (courses.some(c => c.id === id)) {
      setError('A course with that name already exists.');
      return;
    }
    await saveCourses([...courses, { id, label, color: addingColor, autoFire: addingAutoFire, autoFireDelayMinutes: addingDelayMinutes }]);
    setAddingLabel('');
    setAddingColor(COURSE_COLORS[0]);
    setAddingAutoFire(false);
    setAddingDelayMinutes(0);
    setShowAddForm(false);
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading…</p>;

  return (
    <div className="space-y-5 max-w-2xl">
      {error && <p className="text-sm text-red-600">{error}</p>}

      <Card>
        <SectionHeader
          title="Course Firing"
          description="Group menu items into courses and control when each course is sent to the kitchen. Restaurants that don't use this can leave it off — no course UI will appear in the POS."
        />
        <SettingRow
          label="Enable Courses"
          description="Servers assign items to courses and fire each course independently. Auto-fire courses (e.g. Appetizers) fire immediately when the ticket is sent."
          value={coursesEnabled}
          onToggle={handleToggleEnabled}
          saving={saving}
        />
      </Card>

      {coursesEnabled && (
        <Card>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-neutral-800">Course List</p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => saveCourses(DEFAULT_COURSES)}
                disabled={saving}
                className="text-xs text-neutral-400 hover:text-neutral-600 transition-colors disabled:opacity-40"
              >
                Restore defaults
              </button>
              <button
                onClick={() => setShowAddForm(v => !v)}
                className="px-3 py-1 text-xs font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 transition-colors"
              >
                + Add Course
              </button>
            </div>
          </div>

          <p className="text-xs text-neutral-400 mb-4">
            Courses fire top → bottom. <span className="text-emerald-600 font-medium">Auto-fire</span> courses go immediately when a ticket is sent. <span className="text-neutral-500 font-medium">Manual fire</span> courses hold until the server taps Fire — or auto-fire after the delay you set (0 = no auto-fire).
          </p>

          <div className="space-y-2">
            {courses.length === 0 && (
              <p className="text-sm text-neutral-400 text-center py-4">No courses yet. Add one or restore defaults.</p>
            )}
            {courses.map((course, i) => (
              <div key={course.id} className="flex items-center gap-2 p-3 rounded-lg border border-neutral-100 bg-neutral-50">
                {/* Color dot */}
                <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: course.color }} />

                {/* Label */}
                <span className="text-sm font-medium text-neutral-800 flex-1 min-w-0 truncate">{course.label}</span>

                {/* Auto-fire toggle */}
                <button
                  onClick={() => handleUpdate(i, 'autoFire', !course.autoFire)}
                  disabled={saving}
                  className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors shrink-0 disabled:opacity-50 ${
                    course.autoFire ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                  }`}
                >
                  {course.autoFire ? 'Auto-fire' : 'Manual'}
                </button>

                {/* Auto-fire delay — only for Manual courses; fires automatically after N min of holding */}
                {!course.autoFire && (
                  <div className="flex items-center gap-1 shrink-0">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={course.autoFireDelayMinutes ?? 0}
                      onChange={e => handleUpdate(i, 'autoFireDelayMinutes', Math.max(0, parseInt(e.target.value) || 0))}
                      disabled={saving}
                      className="w-12 text-xs text-center border border-neutral-200 rounded-md px-1 py-1 focus:outline-none focus:ring-1 focus:ring-neutral-400 disabled:opacity-40"
                    />
                    <span className="text-xs text-neutral-400">min</span>
                  </div>
                )}

                {/* Color swatches */}
                <div className="hidden sm:flex gap-1 shrink-0">
                  {COURSE_COLORS.slice(0, 5).map(c => (
                    <button
                      key={c}
                      onClick={() => handleUpdate(i, 'color', c)}
                      className="w-4 h-4 rounded-full border-2 transition-all"
                      style={{ backgroundColor: c, borderColor: course.color === c ? '#111' : 'transparent' }}
                    />
                  ))}
                </div>

                {/* Move */}
                <div className="flex gap-0.5 shrink-0">
                  <button onClick={() => handleMoveUp(i)} disabled={i === 0 || saving} className="w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-700 disabled:opacity-20 text-xs">▲</button>
                  <button onClick={() => handleMoveDown(i)} disabled={i === courses.length - 1 || saving} className="w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-700 disabled:opacity-20 text-xs">▼</button>
                </div>

                {/* Remove */}
                <button
                  onClick={() => handleRemove(i)}
                  disabled={saving}
                  className="text-neutral-300 hover:text-red-500 transition-colors text-sm shrink-0 disabled:opacity-40"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          {showAddForm && (
            <div className="mt-4 pt-4 border-t border-neutral-100 space-y-3">
              <p className="text-sm font-semibold text-neutral-700">New Course</p>
              <input
                type="text"
                value={addingLabel}
                onChange={e => setAddingLabel(e.target.value)}
                placeholder="Course name (e.g. Soup & Salad)"
                className="w-full px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-neutral-300"
                onKeyDown={e => e.key === 'Enter' && handleAdd()}
              />
              <div className="flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neutral-500">Color:</span>
                  <div className="flex gap-1">
                    {COURSE_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setAddingColor(c)}
                        className="w-5 h-5 rounded-full border-2 transition-all"
                        style={{ backgroundColor: c, borderColor: addingColor === c ? '#111' : 'transparent' }}
                      />
                    ))}
                  </div>
                </div>
                <button
                  onClick={() => setAddingAutoFire(v => !v)}
                  className={`text-xs font-semibold px-2 py-1 rounded-md transition-colors ${
                    addingAutoFire ? 'bg-emerald-100 text-emerald-700' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
                  }`}
                >
                  {addingAutoFire ? 'Auto-fire' : 'Manual fire'}
                </button>
                {!addingAutoFire && (
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={addingDelayMinutes}
                      onChange={e => setAddingDelayMinutes(Math.max(0, parseInt(e.target.value) || 0))}
                      className="w-14 text-xs text-center border border-neutral-300 rounded-lg px-1 py-1 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                    <span className="text-xs text-neutral-500">min auto-fire delay</span>
                  </div>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleAdd}
                  disabled={saving || !addingLabel.trim()}
                  className="px-4 py-2 text-sm font-semibold bg-neutral-900 text-white rounded-lg hover:bg-neutral-700 disabled:opacity-40 transition-colors"
                >
                  {saving ? 'Saving…' : 'Add Course'}
                </button>
                <button
                  onClick={() => { setShowAddForm(false); setAddingLabel(''); }}
                  className="px-4 py-2 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

// ── State tax / payroll defaults lookup ───────────────────────────────────────
// taxRate is the state-level rate as a percentage (e.g. 6.25 for 6.25%).
// Local/county surtaxes may apply on top — owner should override to their effective rate.
// Minimum wages and tip credit reflect 2024/2025 values; verify annually.
const STATE_TAX_DATA = {
  AL: { name: 'Alabama',        taxRate: 4.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  AK: { name: 'Alaska',         taxRate: 0.00, tippedMin: 11.91, regularMin: 11.91, tipCredit: false },
  AZ: { name: 'Arizona',        taxRate: 5.60, tippedMin: 10.35, regularMin: 14.35, tipCredit: true  },
  AR: { name: 'Arkansas',       taxRate: 6.50, tippedMin: 2.63,  regularMin: 11.00, tipCredit: true  },
  CA: { name: 'California',     taxRate: 7.25, tippedMin: 16.50, regularMin: 16.50, tipCredit: false },
  CO: { name: 'Colorado',       taxRate: 2.90, tippedMin: 11.40, regularMin: 14.42, tipCredit: true  },
  CT: { name: 'Connecticut',    taxRate: 6.35, tippedMin: 9.15,  regularMin: 16.35, tipCredit: true  },
  DE: { name: 'Delaware',       taxRate: 0.00, tippedMin: 2.23,  regularMin: 13.25, tipCredit: true  },
  FL: { name: 'Florida',        taxRate: 6.00, tippedMin: 8.98,  regularMin: 12.00, tipCredit: true  },
  GA: { name: 'Georgia',        taxRate: 4.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  HI: { name: 'Hawaii',         taxRate: 4.00, tippedMin: 10.10, regularMin: 14.00, tipCredit: true  },
  ID: { name: 'Idaho',          taxRate: 6.00, tippedMin: 3.35,  regularMin: 7.25,  tipCredit: true  },
  IL: { name: 'Illinois',       taxRate: 6.25, tippedMin: 8.40,  regularMin: 14.00, tipCredit: true  },
  IN: { name: 'Indiana',        taxRate: 7.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  IA: { name: 'Iowa',           taxRate: 6.00, tippedMin: 4.35,  regularMin: 7.25,  tipCredit: true  },
  KS: { name: 'Kansas',         taxRate: 6.50, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  KY: { name: 'Kentucky',       taxRate: 6.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  LA: { name: 'Louisiana',      taxRate: 4.45, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  ME: { name: 'Maine',          taxRate: 5.50, tippedMin: 7.08,  regularMin: 14.65, tipCredit: true  },
  MD: { name: 'Maryland',       taxRate: 6.00, tippedMin: 3.63,  regularMin: 15.00, tipCredit: true  },
  MA: { name: 'Massachusetts',  taxRate: 6.25, tippedMin: 6.75,  regularMin: 15.00, tipCredit: true  },
  MI: { name: 'Michigan',       taxRate: 6.00, tippedMin: 3.84,  regularMin: 10.33, tipCredit: true  },
  MN: { name: 'Minnesota',      taxRate: 6.88, tippedMin: 10.85, regularMin: 10.85, tipCredit: false },
  MS: { name: 'Mississippi',    taxRate: 7.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  MO: { name: 'Missouri',       taxRate: 4.23, tippedMin: 6.15,  regularMin: 12.30, tipCredit: true  },
  MT: { name: 'Montana',        taxRate: 0.00, tippedMin: 10.30, regularMin: 10.30, tipCredit: false },
  NE: { name: 'Nebraska',       taxRate: 5.50, tippedMin: 2.13,  regularMin: 12.00, tipCredit: true  },
  NV: { name: 'Nevada',         taxRate: 6.85, tippedMin: 12.00, regularMin: 12.00, tipCredit: false },
  NH: { name: 'New Hampshire',  taxRate: 0.00, tippedMin: 3.26,  regularMin: 7.25,  tipCredit: true  },
  NJ: { name: 'New Jersey',     taxRate: 6.63, tippedMin: 5.62,  regularMin: 15.49, tipCredit: true  },
  NM: { name: 'New Mexico',     taxRate: 5.13, tippedMin: 3.00,  regularMin: 12.00, tipCredit: true  },
  NY: { name: 'New York',       taxRate: 4.00, tippedMin: 10.00, regularMin: 16.00, tipCredit: true  },
  NC: { name: 'North Carolina', taxRate: 4.75, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  ND: { name: 'North Dakota',   taxRate: 5.00, tippedMin: 4.86,  regularMin: 7.25,  tipCredit: true  },
  OH: { name: 'Ohio',           taxRate: 5.75, tippedMin: 5.25,  regularMin: 10.45, tipCredit: true  },
  OK: { name: 'Oklahoma',       taxRate: 4.50, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  OR: { name: 'Oregon',         taxRate: 0.00, tippedMin: 14.70, regularMin: 14.70, tipCredit: false },
  PA: { name: 'Pennsylvania',   taxRate: 6.00, tippedMin: 2.83,  regularMin: 7.25,  tipCredit: true  },
  RI: { name: 'Rhode Island',   taxRate: 7.00, tippedMin: 3.89,  regularMin: 15.00, tipCredit: true  },
  SC: { name: 'South Carolina', taxRate: 6.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  SD: { name: 'South Dakota',   taxRate: 4.50, tippedMin: 5.30,  regularMin: 10.59, tipCredit: true  },
  TN: { name: 'Tennessee',      taxRate: 7.00, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  TX: { name: 'Texas',          taxRate: 6.25, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  UT: { name: 'Utah',           taxRate: 6.10, tippedMin: 2.13,  regularMin: 7.25,  tipCredit: true  },
  VT: { name: 'Vermont',        taxRate: 6.00, tippedMin: 6.84,  regularMin: 13.67, tipCredit: true  },
  VA: { name: 'Virginia',       taxRate: 5.30, tippedMin: 10.00, regularMin: 12.00, tipCredit: true  },
  WA: { name: 'Washington',     taxRate: 6.50, tippedMin: 16.28, regularMin: 16.28, tipCredit: false },
  WV: { name: 'West Virginia',  taxRate: 6.00, tippedMin: 2.62,  regularMin: 8.75,  tipCredit: true  },
  WI: { name: 'Wisconsin',      taxRate: 5.00, tippedMin: 2.33,  regularMin: 7.25,  tipCredit: true  },
  WY: { name: 'Wyoming',        taxRate: 4.00, tippedMin: 2.13,  regularMin: 5.15,  tipCredit: true  },
  DC: { name: 'Washington D.C.',taxRate: 6.00, tippedMin: 8.00,  regularMin: 17.50, tipCredit: true  },
};

// ── Billing Tab ───────────────────────────────────────────────────────────────

const BillingTab = ({ restaurantId }) => {
  const [fees, setFees]         = useState(null);
  const [smsCount, setSmsCount] = useState(null);
  const [mmsCount, setMmsCount] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Service charge state
  const [sc, setSc] = useState({
    enabled: false,
    chargeType: 'percentage',
    percentage: 18,
    flatAmount: 0,
    label: 'Service Charge',
    autoApplyPartySize: 8,
    applicability: 'dine_in',
    distributionMode: 'server',
    shiftPoolRoles: [],
  });
  const [scSaving, setScSaving]   = useState(false);
  const [scStatus, setScStatus]   = useState('');

  // Tax settings state
  const [taxSettings, setTaxSettings] = useState({ state: '', taxRate: '', serviceChargeTaxable: true });
  const [taxSaving, setTaxSaving]     = useState(false);
  const [taxStatus, setTaxStatus]     = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (snap.exists()) {
          const d = snap.data();
          console.log('[Billing] Full restaurant doc for', restaurantId, JSON.stringify(d, null, 2));
          console.log('[Billing] platformFees:', JSON.stringify(d.platformFees, null, 2));
          setFees(d.platformFees || {});
          const saved = d.serviceCharge;
          if (saved) setSc(prev => ({ ...prev, ...saved }));
          setTaxSettings({
            state:                d.state                ?? '',
            taxRate:              d.taxRate != null ? String(Number(d.taxRate) * 100) : '',
            serviceChargeTaxable: d.serviceChargeTaxable ?? true,
          });
        } else {
          console.warn('[Billing] No restaurant doc found for', restaurantId);
        }

        const now = new Date();
        const billingPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        console.log('[Billing] Querying sms_logs for billingPeriod:', billingPeriod);
        const logsSnap = await getDocs(
          query(collection(db, 'restaurants_data', restaurantId, 'sms_logs'),
                where('billingPeriod', '==', billingPeriod))
        );
        let totalSms = 0, totalMms = 0;
        logsSnap.forEach(d => {
          const ld = d.data();
          if (ld.status !== 'skipped_optout' && ld.status !== 'failed') {
            totalSms += ld.numSegments || 0;
            totalMms += ld.numMedia    || 0;
          }
        });
        setSmsCount(totalSms);
        setMmsCount(totalMms);
      } catch (err) {
        console.error('[Billing] Load failed:', err);
        setError('Failed to load billing information.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  const handleSaveServiceCharge = async () => {
    setScSaving(true);
    setScStatus('');
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        serviceCharge: {
          enabled:            sc.enabled,
          chargeType:         sc.chargeType || 'percentage',
          percentage:         Number(sc.percentage) || 0,
          flatAmount:         Number(sc.flatAmount)  || 0,
          label:              sc.label || 'Service Charge',
          autoApplyPartySize: Number(sc.autoApplyPartySize) || 0,
          applicability:      sc.applicability || 'dine_in',
          distributionMode:   sc.distributionMode || 'server',
          shiftPoolRoles:     Array.isArray(sc.shiftPoolRoles) ? sc.shiftPoolRoles : [],
        },
      });
      setScStatus('✓ Saved');
    } catch (e) {
      setScStatus(`Error: ${e.message}`);
    } finally {
      setScSaving(false);
      setTimeout(() => setScStatus(''), 3000);
    }
  };

  const handleSaveTaxSettings = async () => {
    setTaxSaving(true);
    setTaxStatus('');
    try {
      const taxRateDecimal = taxSettings.taxRate !== '' ? Number(taxSettings.taxRate) / 100 : 0;
      if (isNaN(taxRateDecimal) || taxRateDecimal < 0 || taxRateDecimal > 1) {
        setTaxStatus('Error: Tax rate must be between 0 and 100');
        return;
      }
      await updateDoc(doc(db, 'restaurants_data', restaurantId), {
        state:                taxSettings.state || '',
        taxRate:              taxRateDecimal,
        serviceChargeTaxable: taxSettings.serviceChargeTaxable,
      });
      setTaxStatus('✓ Saved');
    } catch (e) {
      setTaxStatus(`Error: ${e.message}`);
    } finally {
      setTaxSaving(false);
      setTimeout(() => setTaxStatus(''), 3000);
    }
  };

  const handleApplyStateDefaults = async () => {
    const stateData = STATE_TAX_DATA[taxSettings.state];
    if (!stateData) return;
    try {
      await setDoc(
        doc(db, 'restaurants_data', restaurantId, 'payrollSettings', 'main'),
        {
          tippedMinimumWage:  stateData.tippedMin,
          regularMinimumWage: stateData.regularMin,
          tipCreditEnabled:   stateData.tipCredit,
          state:              taxSettings.state,
        },
        { merge: true }
      );
      setTaxStatus('✓ Payroll defaults applied');
      setTimeout(() => setTaxStatus(''), 3000);
    } catch (e) {
      setTaxStatus(`Error: ${e.message}`);
    }
  };

  if (loading) return <p className="text-sm text-neutral-400 text-center py-12">Loading…</p>;
  if (error)   return <p className="text-sm text-red-500 text-center py-12">{error}</p>;

  const baseFeeCents    = fees?.baseFeeCents          || 0;
  const discountCents   = fees?.referralDiscountCents || 0;
  const effectiveCents  = Math.max(0, baseFeeCents - discountCents);

  const rawExpiry      = fees?.referralDiscountExpiresAt;
  const expiresDate    = rawExpiry
    ? (rawExpiry.toDate ? rawExpiry.toDate() : new Date(rawExpiry))
    : null;
  const discountActive = discountCents > 0 && (!expiresDate || expiresDate > new Date());

  const fmtCents = (c) => `$${(c / 100).toFixed(2)}`;
  const fmtDate  = (d) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const billingPeriodLabel = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="max-w-2xl space-y-4">

      {/* Transaction fee card */}
      <Card>
        <SectionHeader
          title="Per-Transaction Fees"
          description="Every card payment involves two separate fees: GoHost's flat platform fee and Stripe's payment processing fee. These are collected independently — GoHost does not receive any portion of Stripe's fee."
        />

        {/* GoHost Platform Fee */}
        <div className="mb-4">
          <p className="text-xs font-semibold text-brand-blue uppercase tracking-wide mb-1">GoHost Platform Fee</p>
          <p className="text-xs text-neutral-400 mb-2">Flat fee per transaction, collected by GoHost. Not a percentage of the sale.</p>
          <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-neutral-500">Base rate</span>
              <span className="text-sm font-semibold text-neutral-800">{fmtCents(baseFeeCents)} / transaction</span>
            </div>

            {discountCents > 0 && (
              <div className="flex items-center justify-between px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-500">Referral discount</span>
                  {!discountActive && (
                    <span className="text-xs font-medium text-red-500 bg-red-50 px-1.5 py-0.5 rounded">Expired</span>
                  )}
                </div>
                <span className={`text-sm font-semibold ${discountActive ? 'text-emerald-600' : 'text-neutral-300 line-through'}`}>
                  −{fmtCents(discountCents)}
                </span>
              </div>
            )}

            {discountActive && expiresDate && (
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-sm text-neutral-500">Discount expires</span>
                <span className="text-sm font-medium text-amber-600">{fmtDate(expiresDate)}</span>
              </div>
            )}

            {discountActive && !expiresDate && (
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-sm text-neutral-500">Discount expires</span>
                <span className="text-sm font-medium text-emerald-600">No expiry</span>
              </div>
            )}

            <div className="flex items-center justify-between px-3 py-2.5 bg-neutral-50 rounded-b-lg">
              <span className="text-sm font-semibold text-neutral-700">Your effective rate</span>
              <span className="text-sm font-bold text-brand-blue">{fmtCents(effectiveCents)} / transaction</span>
            </div>
          </div>
        </div>

        {/* Stripe Processing Fee */}
        <div>
          <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-1">Stripe Processing Fee</p>
          <p className="text-xs text-neutral-400 mb-2">Charged by Stripe directly. GoHost does not collect or profit from this fee.</p>
          <div className="divide-y divide-neutral-100 border border-neutral-100 rounded-lg">
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-neutral-500">In-person (Terminal / card-present)</span>
              <span className="text-sm font-semibold text-neutral-800">2.7% + $0.05</span>
            </div>
            <div className="flex items-center justify-between px-3 py-2.5">
              <span className="text-sm text-neutral-500">Online (card-not-present)</span>
              <span className="text-sm font-semibold text-neutral-800">2.9% + $0.30</span>
            </div>
          </div>
        </div>

        {/* Example breakdown */}
        <div className="mt-4 bg-neutral-50 rounded-lg px-3 py-3">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Example — $100 in-person payment</p>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-neutral-500">
              <span>Stripe fee (2.7% + $0.05)</span>
              <span>−$2.75</span>
            </div>
            <div className="flex justify-between text-xs text-neutral-500">
              <span>GoHost fee</span>
              <span>−{fmtCents(effectiveCents)}</span>
            </div>
            <div className="flex justify-between text-xs font-semibold text-neutral-700 border-t border-neutral-200 pt-1 mt-1">
              <span>Restaurant receives</span>
              <span>${(100 - 2.75 - effectiveCents / 100).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </Card>

      {/* SMS / MMS usage card */}
      <Card>
        <SectionHeader
          title="SMS / MMS Usage"
          description={`Current billing period: ${billingPeriodLabel}. Charged automatically to your GoHost billing account via Stripe on the 2nd of each month.`}
        />
        <div className="divide-y divide-neutral-100">
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-neutral-500">SMS segments sent</span>
            <span className="text-sm font-semibold text-neutral-800">{smsCount ?? '—'}</span>
          </div>
          <div className="flex items-center justify-between py-2.5">
            <span className="text-sm text-neutral-500">MMS messages sent</span>
            <span className="text-sm font-semibold text-neutral-800">{mmsCount ?? '—'}</span>
          </div>
        </div>
        <p className="text-xs text-neutral-400 mt-3 leading-relaxed">
          A single SMS may count as multiple segments if it exceeds 160 characters. Image messages (MMS) are counted separately. Usage is reported to Stripe at the end of each billing period.
        </p>
      </Card>

      {/* Service Charge card */}
      <Card>
        <SectionHeader
          title="Service Charge"
          description="A mandatory charge added to large-party checks (e.g. 18% auto-gratuity for parties of 8+). Unlike tips, service charges are employer wages — they appear on W-2 as regular income, not tip income."
        />
        <div className="space-y-4">

          {/* Enable toggle */}
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-sm font-semibold text-neutral-800">Enable Service Charge</p>
              <p className="text-xs text-neutral-400 mt-0.5">Auto-applies to qualifying checks at checkout</p>
            </div>
            <div
              onClick={() => setSc(p => ({ ...p, enabled: !p.enabled }))}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${sc.enabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
            >
              <div className={`absolute top-1.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${sc.enabled ? 'translate-x-6' : 'translate-x-1.5'}`} />
            </div>
          </label>

          {sc.enabled && (
            <div className="space-y-4 pt-1 border-t border-neutral-100">

              {/* Charge type toggle */}
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">
                  Charge Type
                </label>
                <div className="flex gap-2">
                  {[
                    { value: 'percentage', label: 'Percentage (%)' },
                    { value: 'flat',       label: 'Flat Amount ($)' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSc(p => ({ ...p, chargeType: opt.value }))}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        sc.chargeType === opt.value
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  {sc.chargeType === 'flat' ? (
                    <>
                      <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                        Flat Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-2 text-sm text-neutral-400">$</span>
                        <input
                          type="number" min="0" step="0.01"
                          className="w-full border border-neutral-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          value={sc.flatAmount}
                          onChange={e => setSc(p => ({ ...p, flatAmount: e.target.value }))}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                        Percentage
                      </label>
                      <div className="relative">
                        <input
                          type="number" min="0" max="100" step="0.5"
                          className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm pr-8 focus:outline-none focus:ring-2 focus:ring-brand-blue"
                          value={sc.percentage}
                          onChange={e => setSc(p => ({ ...p, percentage: e.target.value }))}
                        />
                        <span className="absolute right-3 top-2 text-sm text-neutral-400">%</span>
                      </div>
                    </>
                  )}
                </div>

                <div>
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                    Label on Receipt
                  </label>
                  <input
                    type="text"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="Service Charge"
                    value={sc.label}
                    onChange={e => setSc(p => ({ ...p, label: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                    Auto-Apply Party Size
                  </label>
                  <input
                    type="number" min="0"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    placeholder="8"
                    value={sc.autoApplyPartySize}
                    onChange={e => setSc(p => ({ ...p, autoApplyPartySize: e.target.value }))}
                  />
                  <p className="text-xs text-neutral-400 mt-1">Enter 0 for manual-only (staff adds it)</p>
                </div>

                <div>
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                    Applies To
                  </label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={sc.applicability}
                    onChange={e => setSc(p => ({ ...p, applicability: e.target.value }))}
                  >
                    <option value="dine_in">Dine-In Only</option>
                    <option value="all">All Orders</option>
                    <option value="manual">Manual Only (no auto-apply)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-2">
                  Distribution Mode
                </label>
                <div className="flex gap-2 flex-wrap mb-3">
                  {[
                    { value: 'server',     label: 'Server Gets It' },
                    { value: 'shift_pool', label: 'Shift Pool' },
                    { value: 'revenue',    label: 'House Revenue' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setSc(p => ({ ...p, distributionMode: opt.value }))}
                      className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                        sc.distributionMode === opt.value
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>

                {/* Contextual explanation per mode */}
                {(sc.distributionMode === 'server' || sc.distributionMode === 'wages') && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1.5">
                    <p className="font-semibold">Server Gets It — how it works:</p>
                    <ul className="space-y-1 list-disc list-inside text-blue-800">
                      <li><span className="font-medium">Who receives it:</span> The server who ran that specific check receives 100% of the service charge from that check. It is never pooled or split with other staff.</li>
                      <li><span className="font-medium">When it posts:</span> Immediately when the payment is captured at the POS. Each check settles independently.</li>
                      <li><span className="font-medium">Payroll:</span> Appears on that server's pay period as "Service Charge Income" — separate from hourly wages and tips. W-2 Box 1 (regular wages), not Box 7 (tips).</li>
                      <li><span className="font-medium">Best for:</span> Restaurants where servers work their own sections and the auto-gratuity is intended to reward the individual server directly.</li>
                    </ul>
                    <p className="text-blue-700 pt-0.5 border-t border-blue-200">
                      <span className="font-semibold">IRS:</span> Service charges are employer-controlled wages (not voluntary tips). Always reported as W-2 Box 1, subject to all payroll taxes.
                    </p>
                  </div>
                )}

                {sc.distributionMode === 'shift_pool' && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-900 space-y-2">
                    <p className="font-semibold">Shift Pool — how it works:</p>
                    <ul className="space-y-1 list-disc list-inside text-emerald-800">
                      <li><span className="font-medium">Accumulation:</span> Every service charge collected during the shift goes into a shared pool — no individual employee is credited at the time of payment.</li>
                      <li><span className="font-medium">Distribution:</span> When the shift is closed, the total pool is divided among all eligible employees proportionally by hours worked that shift. An employee who worked 6 of 20 total hours receives 30% of the pool.</li>
                      <li><span className="font-medium">Role filter:</span> If you specify eligible roles below, only employees in those roles share the pool. Leave blank to include everyone clocked in.</li>
                      <li><span className="font-medium">Payroll:</span> Each employee receives a "Service Charge Income" line on their pay period at shift close. W-2 Box 1 (regular wages), not Box 7 (tips).</li>
                      <li><span className="font-medium">Best for:</span> Team-service restaurants (servers + bussers + food runners all contribute to large-party experiences).</li>
                    </ul>
                    <p className="text-emerald-700 pt-0.5 border-t border-emerald-200">
                      <span className="font-semibold">IRS:</span> Service charges are employer-controlled wages (not voluntary tips). Always reported as W-2 Box 1, subject to all payroll taxes.
                    </p>

                    {/* Role filter */}
                    <div className="pt-1">
                      <label className="text-xs font-semibold text-emerald-800 block mb-1">
                        Eligible Roles <span className="font-normal text-emerald-600">(leave blank to include all clocked-in employees)</span>
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. server, busser, food runner"
                        value={(sc.shiftPoolRoles || []).join(', ')}
                        onChange={e => {
                          const roles = e.target.value
                            .split(',')
                            .map(r => r.trim().toLowerCase())
                            .filter(Boolean);
                          setSc(p => ({ ...p, shiftPoolRoles: roles }));
                        }}
                        className="w-full border border-emerald-300 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                      />
                      <p className="text-emerald-600 mt-1">Enter comma-separated role names exactly as they appear on employee profiles.</p>
                    </div>
                  </div>
                )}

                {sc.distributionMode === 'revenue' && (
                  <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-xs text-neutral-700 space-y-1.5">
                    <p className="font-semibold">House Revenue — how it works:</p>
                    <ul className="space-y-1 list-disc list-inside text-neutral-600">
                      <li><span className="font-medium">Who receives it:</span> The restaurant keeps 100% of all service charges collected. No amount posts to any employee's payroll.</li>
                      <li><span className="font-medium">Common uses:</span> Offsetting labor costs, covering credit card processing fees, funding kitchen or back-of-house compensation outside the payroll system.</li>
                      <li><span className="font-medium">Payroll impact:</span> Zero — service charges do not appear on any employee's pay period summary when this mode is active.</li>
                      <li><span className="font-medium">Best for:</span> Restaurants that use service charges as a business revenue tool rather than employee compensation.</li>
                    </ul>
                    <p className="text-neutral-500 pt-0.5 border-t border-neutral-200">
                      <span className="font-semibold">IRS:</span> Even kept as house revenue, service charges are employer income (not tips) and must be reported accordingly on business tax filings.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSaveServiceCharge}
              disabled={scSaving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {scSaving ? 'Saving…' : 'Save Service Charge Settings'}
            </button>
            {scStatus && (
              <span className={`text-sm font-medium ${scStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {scStatus}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Tax & State Settings */}
      <Card>
        <SectionHeader
          title="Tax & State Settings"
          description="Configure your state and local sales tax rate. Applied to dine-in checks at checkout."
        />
        <div className="space-y-4">

          {/* State selector */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-1">State</label>
            <select
              value={taxSettings.state}
              onChange={e => setTaxSettings(prev => ({ ...prev, state: e.target.value }))}
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">— Select state —</option>
              {Object.entries(STATE_TAX_DATA).sort(([,a],[,b]) => a.name.localeCompare(b.name)).map(([code, d]) => (
                <option key={code} value={code}>{d.name}</option>
              ))}
            </select>
            {taxSettings.state && STATE_TAX_DATA[taxSettings.state] && (
              <p className="text-xs text-neutral-400 mt-1">
                State default: {STATE_TAX_DATA[taxSettings.state].taxRate.toFixed(2)}% sales tax &bull; Regular min ${STATE_TAX_DATA[taxSettings.state].regularMin.toFixed(2)}/hr &bull; Tipped min ${STATE_TAX_DATA[taxSettings.state].tippedMin.toFixed(2)}/hr
                {!STATE_TAX_DATA[taxSettings.state].tipCredit && ' (no tip credit)'}
              </p>
            )}
          </div>

          {/* Tax rate override */}
          <div>
            <label className="block text-sm font-medium text-neutral-600 mb-1">
              Effective Sales Tax Rate (%)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="20"
                step="0.001"
                value={taxSettings.taxRate}
                onChange={e => setTaxSettings(prev => ({ ...prev, taxRate: e.target.value }))}
                placeholder={taxSettings.state && STATE_TAX_DATA[taxSettings.state]
                  ? String(STATE_TAX_DATA[taxSettings.state].taxRate)
                  : '0'}
                className="w-32 border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              />
              <span className="text-sm text-neutral-400">%</span>
              {taxSettings.state && STATE_TAX_DATA[taxSettings.state] && (
                <button
                  type="button"
                  onClick={() => setTaxSettings(prev => ({
                    ...prev,
                    taxRate: String(STATE_TAX_DATA[taxSettings.state].taxRate),
                  }))}
                  className="text-xs text-brand-blue underline"
                >
                  Use state default
                </button>
              )}
            </div>
            <p className="text-xs text-neutral-400 mt-1">
              Enter your combined rate (state + county + local). Overrides the state default if different.
            </p>
          </div>

          {/* Service charge taxable toggle */}
          <div className="flex items-center justify-between py-2 border-t border-neutral-100">
            <div>
              <p className="text-sm font-medium text-neutral-700">Service Charge is Taxable</p>
              <p className="text-xs text-neutral-400 mt-0.5">Most states tax service charges. Disable only if your state exempts them.</p>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <button
                onClick={() => setTaxSettings(prev => ({ ...prev, serviceChargeTaxable: true }))}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${taxSettings.serviceChargeTaxable ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
              >Yes</button>
              <button
                onClick={() => setTaxSettings(prev => ({ ...prev, serviceChargeTaxable: false }))}
                className={`px-3 py-1.5 text-sm font-semibold rounded-lg transition-colors ${!taxSettings.serviceChargeTaxable ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'}`}
              >No</button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSaveTaxSettings}
              disabled={taxSaving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {taxSaving ? 'Saving…' : 'Save Tax Settings'}
            </button>
            {taxSettings.state && STATE_TAX_DATA[taxSettings.state] && (
              <button
                onClick={handleApplyStateDefaults}
                disabled={taxSaving}
                className="px-4 py-2.5 text-sm font-semibold bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 disabled:opacity-40"
              >
                Apply State Defaults to Payroll
              </button>
            )}
            {taxStatus && (
              <span className={`text-sm font-medium ${taxStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {taxStatus}
              </span>
            )}
          </div>

          <p className="text-xs text-neutral-400 pt-1">
            Tax applies to food + drinks at checkout. Tips are never taxed. "Apply State Defaults to Payroll" updates minimum wage and tip credit settings in your Payroll tab.
          </p>
        </div>
      </Card>

    </div>
  );
};

// ── Payroll Compliance Tab ────────────────────────────────────────────────────

const generate8027Fn = httpsCallable(functions, 'generate8027Report');

export const PayrollTab = ({ restaurantId }) => {
  const [tipMode, setTipMode]           = useState('individual');
  const [tipModeSaving, setTipModeSaving] = useState(false);
  const [tipModeStatus, setTipModeStatus] = useState('');

  const handleSaveTipMode = async (mode) => {
    setTipMode(mode);
    setTipModeSaving(true);
    setTipModeStatus('');
    try {
      await updateDoc(doc(db, 'restaurants_data', restaurantId), { 'tipSettings.mode': mode });
      setTipModeStatus('✓ Saved');
      setTimeout(() => setTipModeStatus(''), 3000);
    } catch (e) {
      setTipModeStatus(`Error: ${e.message}`);
    } finally {
      setTipModeSaving(false);
    }
  };

  const [settings, setSettings] = useState({
    tipCreditEnabled:             false,
    tippedMinimumWage:            2.13,
    regularMinimumWage:           7.25,
    state:                        '',
    overtimeRule:                 'weekly_40',
    overtimeThresholdHours:       40,
    dailyOvertimeThresholdHours:  8,
    overtimeMultiplier:           1.5,
    doubleTimeEnabled:            false,
    doubleTimeThresholdHours:     12,
    doubleTimeMultiplier:         2.0,
    autoMealBreakEnabled:         false,
    autoMealBreakThresholdHours:  6,
    autoMealBreakMinutes:         30,
    frequency:                    'weekly',
    periodStartDay:               1,
    biweeklyAnchorStart:          '2025-01-06',
  });
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saveStatus, setSaveStatus] = useState('');

  // 8027 report
  const [reportYear, setReportYear]   = useState(String(new Date().getFullYear() - 1));
  const [reportRunning, setReportRunning] = useState(false);
  const [reportResult, setReportResult]   = useState(null);
  const [reportError, setReportError]     = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        // Load tipSettings.mode from the main restaurant doc
        const restSnap = await getDoc(doc(db, 'restaurants_data', restaurantId));
        if (restSnap.exists()) {
          const rd = restSnap.data();
          setTipMode(rd.tipSettings?.mode || 'individual');
        }

        const snap = await getDoc(doc(db, 'restaurants_data', restaurantId, 'payrollSettings', 'main'));
        if (snap.exists()) {
          const d = snap.data();
          setSettings(prev => ({
            ...prev,
            tipCreditEnabled:             d.tipCreditEnabled             ?? false,
            tippedMinimumWage:            d.tippedMinimumWage            ?? 2.13,
            regularMinimumWage:           d.regularMinimumWage           ?? 7.25,
            state:                        d.state                        ?? '',
            overtimeRule:                 d.overtimeRule                 ?? 'weekly_40',
            overtimeThresholdHours:       d.overtimeThresholdHours       ?? 40,
            dailyOvertimeThresholdHours:  d.dailyOvertimeThresholdHours  ?? 8,
            overtimeMultiplier:           d.overtimeMultiplier           ?? 1.5,
            doubleTimeEnabled:            d.doubleTimeEnabled            ?? false,
            doubleTimeThresholdHours:     d.doubleTimeThresholdHours     ?? 12,
            doubleTimeMultiplier:         d.doubleTimeMultiplier         ?? 2.0,
            autoMealBreakEnabled:         d.autoMealBreakEnabled         ?? false,
            autoMealBreakThresholdHours:  d.autoMealBreakThresholdHours  ?? 6,
            autoMealBreakMinutes:         d.autoMealBreakMinutes         ?? 30,
            frequency:                    d.frequency                    ?? 'weekly',
            periodStartDay:               d.periodStartDay               ?? 1,
            biweeklyAnchorStart:          d.biweeklyAnchorStart          ?? '2025-01-06',
          }));
        }
      } catch (e) {
        console.warn('[PayrollTab] load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [restaurantId]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('');
    try {
      await setDoc(
        doc(db, 'restaurants_data', restaurantId, 'payrollSettings', 'main'),
        {
          tipCreditEnabled:             settings.tipCreditEnabled,
          tippedMinimumWage:            Number(settings.tippedMinimumWage)           || 2.13,
          regularMinimumWage:           Number(settings.regularMinimumWage)          || 7.25,
          state:                        settings.state                               || '',
          overtimeRule:                 settings.overtimeRule                            || 'weekly_40',
          overtimeThresholdHours:       Number(settings.overtimeThresholdHours)          || 40,
          dailyOvertimeThresholdHours:  Number(settings.dailyOvertimeThresholdHours)     || 8,
          overtimeMultiplier:           Number(settings.overtimeMultiplier)              || 1.5,
          doubleTimeEnabled:            !!settings.doubleTimeEnabled,
          doubleTimeThresholdHours:     Number(settings.doubleTimeThresholdHours)        || 12,
          doubleTimeMultiplier:         Number(settings.doubleTimeMultiplier)            || 2.0,
          autoMealBreakEnabled:         !!settings.autoMealBreakEnabled,
          autoMealBreakThresholdHours:  Number(settings.autoMealBreakThresholdHours)    || 6,
          autoMealBreakMinutes:         Number(settings.autoMealBreakMinutes)           || 30,
          frequency:                    settings.frequency                             || 'weekly',
          periodStartDay:               Number(settings.periodStartDay)                ?? 1,
          biweeklyAnchorStart:          settings.biweeklyAnchorStart                  || '2025-01-06',
        },
        { merge: true }
      );
      setSaveStatus('✓ Saved');
    } catch (e) {
      setSaveStatus(`Error: ${e.message}`);
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleRun8027 = async () => {
    setReportRunning(true);
    setReportResult(null);
    setReportError('');
    try {
      const result = await generate8027Fn({ restaurantId, year: Number(reportYear) });
      setReportResult(result.data);
    } catch (e) {
      setReportError(e.message || 'Failed to generate report.');
    } finally {
      setReportRunning(false);
    }
  };

  if (loading) return <div className="text-sm text-neutral-400 py-8 text-center">Loading…</div>;

  const fmt = (n) => (n ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

  const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  return (
    <div className="max-w-2xl space-y-4">

      {/* Tip Distribution Mode */}
      <Card>
        <SectionHeader
          title="Tip Distribution"
          description="Controls how customer tips are handled across your team."
        />
        <div className="space-y-3">
          <button
            onClick={() => handleSaveTipMode('individual')}
            disabled={tipModeSaving}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors disabled:opacity-50 ${
              tipMode === 'individual'
                ? 'border-brand-blue bg-blue-50'
                : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
          >
            <p className={`text-sm font-semibold ${tipMode === 'individual' ? 'text-brand-blue' : 'text-neutral-800'}`}>
              Individual Tips
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              Each server keeps 100% of the tips they personally collected during their shift. No sharing between staff members.
            </p>
          </button>

          <button
            onClick={() => handleSaveTipMode('pooled')}
            disabled={tipModeSaving}
            className={`w-full text-left px-4 py-3 rounded-xl border-2 transition-colors disabled:opacity-50 ${
              tipMode === 'pooled'
                ? 'border-brand-blue bg-blue-50'
                : 'border-neutral-200 bg-white hover:border-neutral-300'
            }`}
          >
            <p className={`text-sm font-semibold ${tipMode === 'pooled' ? 'text-brand-blue' : 'text-neutral-800'}`}>
              Pooled Tips
            </p>
            <p className="text-xs text-neutral-500 mt-0.5">
              All tips collected during the shift are combined into a shared pool and distributed proportionally by hours worked among eligible staff.
            </p>
          </button>

          {tipModeStatus && (
            <p className={`text-sm font-medium ${tipModeStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
              {tipModeStatus}
            </p>
          )}
        </div>
      </Card>

      {/* Pay Period Configuration card */}
      <Card>
        <SectionHeader
          title="Pay Period"
          description="Controls how pay periods are created and when they start. Changes take effect on the next period — existing open periods are not affected."
        />
        <div className="space-y-4">

          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
              Pay Frequency
            </label>
            <select
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              value={settings.frequency}
              onChange={e => setSettings(p => ({ ...p, frequency: e.target.value }))}
            >
              <option value="weekly">Weekly</option>
              <option value="biweekly">Biweekly (every 2 weeks)</option>
              <option value="monthly">Monthly (1st of month)</option>
            </select>
          </div>

          {settings.frequency !== 'monthly' && (
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                Period Start Day
              </label>
              <select
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                value={settings.periodStartDay}
                onChange={e => setSettings(p => ({ ...p, periodStartDay: Number(e.target.value) }))}
              >
                {DAY_NAMES.map((name, i) => (
                  <option key={i} value={i}>{name}</option>
                ))}
              </select>
              <p className="text-xs text-neutral-400 mt-1">The weekday on which each pay period begins.</p>
            </div>
          )}

          {settings.frequency === 'biweekly' && (
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                Biweekly Anchor Date
              </label>
              <input
                type="date"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                value={settings.biweeklyAnchorStart}
                onChange={e => setSettings(p => ({ ...p, biweeklyAnchorStart: e.target.value }))}
              />
              <p className="text-xs text-neutral-400 mt-1">
                A past period-start date that anchors the biweekly cadence. Must fall on your chosen start day.
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Payroll Settings'}
            </button>
            {saveStatus && (
              <span className={`text-sm font-medium ${saveStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Tip Credit card */}
      <Card>
        <SectionHeader
          title="Tip Credit (FLSA §203m)"
          description="When enabled, GoHost checks at pay-period lock that each tipped employee's effective hourly rate (base wages + tips ÷ hours) meets regular minimum wage. If not, a makeup adjustment entry is added automatically."
        />
        <div className="space-y-4">

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-sm font-semibold text-neutral-800">Enable Tip Credit Enforcement</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                Disable for CA, MN, OR, WA, NV, and other states that prohibit the tip credit
              </p>
            </div>
            <div
              onClick={() => setSettings(p => ({ ...p, tipCreditEnabled: !p.tipCreditEnabled }))}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${settings.tipCreditEnabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
            >
              <div className={`absolute top-1.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.tipCreditEnabled ? 'translate-x-6' : 'translate-x-1.5'}`} />
            </div>
          </label>

          {settings.tipCreditEnabled && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-1 border-t border-neutral-100">
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  Tipped Minimum Wage ($/hr)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-sm text-neutral-400">$</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={settings.tippedMinimumWage}
                    onChange={e => setSettings(p => ({ ...p, tippedMinimumWage: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-neutral-400 mt-1">Federal: $2.13. Many states require more.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  Regular Minimum Wage ($/hr)
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-sm text-neutral-400">$</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg pl-7 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={settings.regularMinimumWage}
                    onChange={e => setSettings(p => ({ ...p, regularMinimumWage: e.target.value }))}
                  />
                </div>
                <p className="text-xs text-neutral-400 mt-1">Federal: $7.25. Set your state/local rate.</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Payroll Settings'}
            </button>
            {saveStatus && (
              <span className={`text-sm font-medium ${saveStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Break Rules card */}
      <Card>
        <SectionHeader
          title="Break Rules"
          description="Auto meal break deduction applies a fixed unpaid break to shifts above a length threshold when no meal break was manually clocked. Most states require a 30-min unpaid break for shifts of 5–6 hours."
        />
        <div className="space-y-4">

          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-sm font-semibold text-neutral-800">Auto Meal Break Deduction</p>
              <p className="text-xs text-neutral-400 mt-0.5">
                Automatically deduct unpaid meal break time at clock-out when no break was logged.
              </p>
            </div>
            <div
              onClick={() => setSettings(p => ({ ...p, autoMealBreakEnabled: !p.autoMealBreakEnabled }))}
              className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${settings.autoMealBreakEnabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
            >
              <div className={`absolute top-1.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.autoMealBreakEnabled ? 'translate-x-6' : 'translate-x-1.5'}`} />
            </div>
          </label>

          {settings.autoMealBreakEnabled && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-1 border-t border-neutral-100">
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  Shift Length Threshold (hours)
                </label>
                <input
                  type="number" min="1" step="0.5"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  value={settings.autoMealBreakThresholdHours}
                  onChange={e => setSettings(p => ({ ...p, autoMealBreakThresholdHours: e.target.value }))}
                />
                <p className="text-xs text-neutral-400 mt-1">Deduction applies only when shift is at least this long.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  Break Deduction (minutes)
                </label>
                <input
                  type="number" min="1" step="1"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  value={settings.autoMealBreakMinutes}
                  onChange={e => setSettings(p => ({ ...p, autoMealBreakMinutes: e.target.value }))}
                />
                <p className="text-xs text-neutral-400 mt-1">Standard: 30 minutes unpaid.</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Payroll Settings'}
            </button>
            {saveStatus && (
              <span className={`text-sm font-medium ${saveStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Overtime Rules card */}
      <Card>
        <SectionHeader
          title="Overtime Rules"
          description="Choose how overtime is calculated. Weekly 40h is standard in most US states. Daily 8h is required in California and a handful of other states."
        />
        <div className="space-y-4">

          <div>
            <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
              Overtime Rule
            </label>
            <select
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
              value={settings.overtimeRule}
              onChange={e => setSettings(p => ({ ...p, overtimeRule: e.target.value }))}
            >
              <option value="none">None — no overtime premium</option>
              <option value="weekly_40">Weekly 40h (Federal / most states)</option>
              <option value="daily_8">Daily 8h (CA, CO, NV and others)</option>
            </select>
          </div>

          {settings.overtimeRule === 'weekly_40' && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 pt-1 border-t border-neutral-100">
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  Weekly OT Threshold (hours)
                </label>
                <input
                  type="number" min="1" step="1"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  value={settings.overtimeThresholdHours}
                  onChange={e => setSettings(p => ({ ...p, overtimeThresholdHours: e.target.value }))}
                />
                <p className="text-xs text-neutral-400 mt-1">Federal standard: 40 hrs/week.</p>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                  OT Multiplier
                </label>
                <input
                  type="number" min="1" step="0.01"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                  value={settings.overtimeMultiplier}
                  onChange={e => setSettings(p => ({ ...p, overtimeMultiplier: e.target.value }))}
                />
                <p className="text-xs text-neutral-400 mt-1">Standard: 1.5× (time-and-a-half).</p>
              </div>
            </div>
          )}

          {settings.overtimeRule === 'daily_8' && (
            <div className="space-y-4 pt-1 border-t border-neutral-100">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                    Daily OT Threshold (hours/day)
                  </label>
                  <input
                    type="number" min="1" step="1"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={settings.dailyOvertimeThresholdHours}
                    onChange={e => setSettings(p => ({ ...p, dailyOvertimeThresholdHours: e.target.value }))}
                  />
                  <p className="text-xs text-neutral-400 mt-1">CA/CO standard: 8 hrs/day.</p>
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                    OT Multiplier (1st tier)
                  </label>
                  <input
                    type="number" min="1" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    value={settings.overtimeMultiplier}
                    onChange={e => setSettings(p => ({ ...p, overtimeMultiplier: e.target.value }))}
                  />
                  <p className="text-xs text-neutral-400 mt-1">Standard: 1.5×.</p>
                </div>
              </div>

              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-semibold text-neutral-800">Enable Double-Time (2×)</p>
                  <p className="text-xs text-neutral-400 mt-0.5">
                    Required in CA for hours beyond 12/day and all hours on the 7th consecutive day.
                  </p>
                </div>
                <div
                  onClick={() => setSettings(p => ({ ...p, doubleTimeEnabled: !p.doubleTimeEnabled }))}
                  className={`relative w-12 h-7 rounded-full transition-colors cursor-pointer ${settings.doubleTimeEnabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <div className={`absolute top-1.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.doubleTimeEnabled ? 'translate-x-6' : 'translate-x-1.5'}`} />
                </div>
              </label>

              {settings.doubleTimeEnabled && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                      Double-Time Threshold (hours/day)
                    </label>
                    <input
                      type="number" min="1" step="1"
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      value={settings.doubleTimeThresholdHours}
                      onChange={e => setSettings(p => ({ ...p, doubleTimeThresholdHours: e.target.value }))}
                    />
                    <p className="text-xs text-neutral-400 mt-1">CA standard: 12 hrs/day.</p>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                      Double-Time Multiplier
                    </label>
                    <input
                      type="number" min="1" step="0.01"
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                      value={settings.doubleTimeMultiplier}
                      onChange={e => setSettings(p => ({ ...p, doubleTimeMultiplier: e.target.value }))}
                    />
                    <p className="text-xs text-neutral-400 mt-1">Standard: 2.0×.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 pt-2 border-t border-neutral-100">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save Payroll Settings'}
            </button>
            {saveStatus && (
              <span className={`text-sm font-medium ${saveStatus.startsWith('Error') ? 'text-red-500' : 'text-emerald-600'}`}>
                {saveStatus}
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* IRS Form 8027 card */}
      <Card>
        <SectionHeader
          title="IRS Form 8027 Report"
          description="Required annually for large food/beverage establishments with 10+ employees on more than 80 days/year. Tracks charged tips, gross receipts, and whether reported tips meet the 8% floor."
        />
        <div className="space-y-4">

          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs font-semibold text-neutral-500 uppercase tracking-wide block mb-1">
                Tax Year
              </label>
              <input
                type="number" min="2020" max={new Date().getFullYear()}
                className="w-28 border border-neutral-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                value={reportYear}
                onChange={e => setReportYear(e.target.value)}
              />
            </div>
            <button
              onClick={handleRun8027}
              disabled={reportRunning}
              className="px-5 py-2.5 text-sm font-semibold bg-brand-blue text-white rounded-lg disabled:opacity-40"
            >
              {reportRunning ? 'Generating…' : 'Generate Report'}
            </button>
          </div>

          {reportError && (
            <p className="text-sm text-red-500">{reportError}</p>
          )}

          {reportResult && (
            <div className="space-y-3">
              {/* Eligibility */}
              <div className={`px-4 py-3 rounded-xl text-sm font-medium ${
                reportResult.eligibleForForm8027
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-emerald-50 text-emerald-800'
              }`}>
                {reportResult.eligibleForForm8027
                  ? `${reportResult.employeeCount} employees — Form 8027 filing likely required`
                  : `${reportResult.employeeCount} employees — Form 8027 may not be required (verify actual headcount for the year)`}
              </div>

              {/* Allocated tips warning */}
              {reportResult.lines.allocatedTipsRequired && (
                <div className="px-4 py-3 bg-red-50 rounded-xl text-sm text-red-700">
                  <p className="font-semibold mb-1">Allocated Tips Required</p>
                  <p>Reported tips ({fmt(reportResult.lines.line3_reportedTips)}) are below the 8% floor ({fmt(reportResult.lines.eighthPercentThreshold)}). You must allocate <strong>{fmt(reportResult.lines.line6_allocatedTips)}</strong> across tipped employees on W-2 Box 8.</p>
                </div>
              )}

              {/* 8027 line items */}
              <div className="border border-neutral-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-neutral-50 border-b border-neutral-200">
                  <p className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Form 8027 Line Items — {reportResult.year}</p>
                </div>
                <div className="divide-y divide-neutral-100">
                  {[
                    { line: '1', label: 'Total charged tips (card/electronic)',     value: fmt(reportResult.lines.line1_chargedTips) },
                    { line: '2', label: 'Total charged receipts',                   value: fmt(reportResult.lines.line2_chargedReceipts) },
                    { line: '3', label: 'Total reported tips (all employees)',       value: fmt(reportResult.lines.line3_reportedTips) },
                    { line: '4', label: 'Gross receipts for the year',              value: fmt(reportResult.lines.line4_grossReceipts) },
                    { line: '6', label: 'Allocated tips (shortfall if < 8%)',       value: fmt(reportResult.lines.line6_allocatedTips) },
                  ].map(row => (
                    <div key={row.line} className="flex items-center justify-between px-4 py-2.5">
                      <span className="text-sm text-neutral-500">Line {row.line} — {row.label}</span>
                      <span className="text-sm font-semibold text-neutral-800">{row.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {reportResult.notes?.length > 0 && (
                <div className="space-y-1">
                  {reportResult.notes.map((note, i) => (
                    <p key={i} className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">{note}</p>
                  ))}
                </div>
              )}

              {!reportResult.financialDataAvailable && (
                <p className="text-xs text-neutral-400">
                  Lines 2 & 4 require financial_ledger entries with timestamps. If these show $0, check that your payment processing is writing to the financial_ledger collection.
                </p>
              )}
            </div>
          )}
        </div>
      </Card>

    </div>
  );
};

// ── Website Tab ───────────────────────────────────────────────────────────────

export default SettingsView;
